import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  OnDestroy,
  inject,
  Input,
  Output,
  EventEmitter,
  signal,
  computed,
  TemplateRef,
  ContentChild,
  ViewChild,
  ViewChildren,
  QueryList,
  ChangeDetectorRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
  FormsModule,
} from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { SelectModule } from 'primeng/select';
import { CheckboxModule } from 'primeng/checkbox';
import { TextareaModule } from 'primeng/textarea';
import { RadioButtonModule } from 'primeng/radiobutton';
import { InputNumberModule } from 'primeng/inputnumber';
import { DatePickerModule } from 'primeng/datepicker';
import { DividerModule } from 'primeng/divider';
import { DialogModule } from 'primeng/dialog';
import { DragDropModule } from 'primeng/dragdrop';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MenuItem, MessageService } from 'primeng/api';
import { MenuModule, Menu } from 'primeng/menu';
import { formatCurrencyVND } from '@app/shared/utils/date-formatter.util';
import {
  DataTableComponent,
  TableColumn,
} from '@shared/components/data-table/data-table.component';
import { AdvancedDataTableComponent } from '@shared/components/advanced-data-table/advanced-data-table.component';
import { Subject, takeUntil, debounceTime, distinctUntilChanged } from 'rxjs';
import { FileUploadComponent } from '../file-upload/file-upload.component';
import { MultiFileUploadComponent } from '../multi-file-upload/multi-file-upload.component';
import { HtmlDescriptionComponent } from '../html-description/html-description.component';
import {
  AppButtonComponent,
  ButtonSeverity,
} from '../app-button/app-button.component';
import {
  GalleriaComponent,
  GalleriaImage,
  GalleriaConfig,
} from '../galleria/galleria.component';
import { TreeSelectModule } from 'primeng/treeselect';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { MultiSelectModule } from 'primeng/multiselect';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { SearchFormComponent } from '@shared/components/search-form/search-form.component';
import {
  StatusFilterComponent,
  ActionButton as StatusActionButton,
} from '@shared/components/status-filter/status-filter.component';
import {
  AdvancedDatepickerComponent,
  DateRange,
} from '../advanced-datepicker/advanced-datepicker.component';
import { TextNormalizerService } from '@shared/services/text-normalizer.service';
import { UnicodeNormalizeDirective } from '@shared/directives/unicode-normalize.directive';
import {
  PaginatedDropdownConfig,
  PaginatedDropdownController,
} from '@shared/utils/paginated-dropdown.util';
import { SpuFaqEditorComponent } from '@app/feature/center-business/goods/shared/spu-faq-editor/spu-faq-editor.component';
import { FaqEditItem } from '@app/feature/center-business/goods/shared/spu-faq-editor/spu-faq.model';

export interface FormField {
  label: string;
  /** Vietnamese label when translateLabel is false (API-driven labels). */
  labelVi?: string;
  /**
   * Small muted helper text shown under the field (plain text, or i18n key when translateHint is true).
   * Used e.g. to explain why a mutually-exclusive field is currently ignored.
   */
  hint?: string;
  /** Vietnamese hint, preferred when UI language is Vietnamese. */
  hintVi?: string;
  /** When true, hint is treated as an i18n key and translated. Defaults to false (plain text). */
  translateHint?: boolean;
  /** When true, hint is shown as an info-icon tooltip on the label instead of under the field. */
  hintAsTooltip?: boolean;
  /**
   * When false, label/labelVi are shown as plain text (no i18n key). Defaults
   * to true for existing forms.
   */
  translateLabel?: boolean;
  controlName: string;
  type:
    | 'text'
    | 'password'
    | 'number'
    | 'currency'
    | 'date'
    | 'dateRange'
    | 'dropdown'
    | 'searchableDropdown'
    | 'warehouseAutocomplete'
    | 'couponAutocomplete'
    | 'treeselect'
    | 'checkbox'
    | 'radio'
    | 'textarea'
    | 'html-description'
    | 'file'
    | 'multi-file'
    | 'image-display'
    | 'galleria-display';
  placeholder?: string;
  /** Vietnamese placeholder when translateLabel is false (API-driven labels). */
  placeholderVi?: string;
  /** i18n interpolation params for `placeholder` when it's a translate key with {{}} tokens. */
  placeholderParams?: Record<string, string | number>;
  emptyMessage?: string;
  options?: {
    label: string;
    value: any;
    labelVi?: string;
    disabled?: boolean;
    loading?: boolean;
  }[];
  showClear?: boolean;
  required?: boolean;
  disabled?: boolean;
  readonly?: boolean;
  hidden?: boolean;
  width?: string;
  colSpan?: number;
  colspan?: number;
  /**
   * controlName of another field in the same section to render inside that field's own grid
   * cell instead of its own — keeps them visually grouped as one unit regardless of sibling
   * cell heights (e.g. a multi-file field growing taller). Supports 'dropdown', 'file', and
   * 'text' as nested field types. Nested fields always stack, in field-array order.
   */
  nestedUnder?: string;
  /** Suppresses the label above a nested field (see nestedUnder) when its placeholder already
   *  conveys the same information. */
  hideLabel?: boolean;
  /** Renders this field's own label and control on one row (label left, control right) instead
   *  of stacked. Only meaningful for 'radio' so far. */
  labelInline?: boolean;
  min?: number;
  max?: number;
  maxLength?: number;
  useGrouping?: boolean;
  decimal?: boolean;
  maxFractionDigits?: number;
  /** Unit suffix for number fields (e.g. ' cm', ' g') - displayed like currency suffix */
  suffix?: string;
  section: string;
  /** Override grid column count for the whole form section (e.g. 4). */
  sectionGridCols?: number;
  defaultValue?: any;
  additionalValue?: any;
  validators?: any[];
  dependsOn?: string;
  autoSelect?: (parentValue: any) => any;
  errorMessages?: { [key: string]: string };
  loadOptions?: (value: any) => Promise<{ label: string; value: any }[]>;
  // Dynamic label configuration
  dynamicLabel?: {
    type: 'coupon' | 'custom' | 'image-count';
    dependsOn?: string;
    rules?: Array<{
      condition: (value: any) => boolean;
      label: string;
    }>;
    singularLabel?: string;
    pluralLabel?: string;
  };
  customData?: any; // For custom field data like SKU detail
  // File upload specific properties
  accept?: string;
  multiple?: boolean;
  maxSize?: number;
  maxFiles?: number;
  acceptedTypes?: string;
  /** multi-file only: enables drag-drop reordering of already-uploaded files in place. */
  reorderable?: boolean;
  // Client-side video pre-validation (fast feedback; server is authoritative)
  videoMinDurationSec?: number;
  videoMaxDurationSec?: number;
  videoMaxWidthPx?: number;
  videoMaxHeightPx?: number;
  /** Text field only: show a YouTube/Vimeo icon hint derived from the field's own value. */
  showVideoPlatformHint?: boolean;

  // Tree/TreeSelect specific properties
  useCheckbox?: boolean;
  treeOptions?: { label: string; value: any; children?: any[] }[];

  // Date picker specific properties
  showTime?: boolean;
  /** When true, picker shows time only (no calendar date). */
  timeOnly?: boolean;
  showSeconds?: boolean;
  hourFormat?: string;
  minDate?: Date | null;
  maxDate?: Date | null;

  // Coupon autocomplete specific properties
  couponChipValue?: string | null;
  onCouponChipRemove?: () => void;
  // Multi-coupon support
  couponChips?: string[];
  onCouponChipRemoveByCode?: (code: string) => void;
  // Add loading property to FormField interface for dropdown fields
  loading?: boolean;
  onFilter?: (filterValue: string) => void;
  onLoadMore?: () => void;
  onLazyLoad?: (event: { first: number; last: number }) => void;
  hasMore?: boolean;
  loadingMore?: boolean;
  filter?: boolean;
  filterBy?: string;
  filterPlaceholder?: string;
  loadMoreLabel?: string;
  virtualScroll?: boolean;
  virtualScrollItemSize?: number;
  autoLoadMore?: boolean;
  /** Server-side paginated options; managed automatically by add-form */
  paginatedOptions?: PaginatedDropdownConfig;
  dynamicMinDate?: boolean;
  dynamicMaxDate?: boolean;

  // Custom styling properties
  customStyles?: {
    height?: string;
    minHeight?: string;
    maxHeight?: string;
    width?: string;
    padding?: string;
    margin?: string;
    fontSize?: string;
    lineHeight?: string;
    backgroundColor?: string;
    borderColor?: string;
    borderRadius?: string;
  };

  /** Password: show reveal toggle even when the control is readonly. */
  passwordToggleWhenReadonly?: boolean;
  /** Password: show a copy-to-clipboard button beside the field. */
  showCopyButton?: boolean;
  /**
   * Password: use readonly only (never disabled) so reveal/copy work in view
   * mode.
   */
  passwordReadonlyOnly?: boolean;

  additionalFields?: {
    show: boolean;
    fields: {
      controlName: string;
      label: string;
      type: string;
      placeholder?: string;
      min?: number;
      max?: number;
    }[];
  };
}

export interface TableSection {
  title: string;
  /** Stable untranslated key used for generated test IDs when title is already translated. */
  testIdKey?: string;
  /** Optional i18n key for callers that keep the display title translated. */
  titleKey?: string;
  name?: string; // Stable identifier for comparison (language-independent)
  show: boolean;
  columns: TableColumn[];
  data: any[];
  loading?: boolean;
  actions?: TableAction[];
  hasCheckbox?: boolean;
  hasSerialColumn?: boolean; // Control serial number column visibility
  selection?: any[];
  totalRecords?: number;
  rows?: number;
  first?: number;
  rowsPerPageOptions?: number[]; // Options for rows per page dropdown
  errorMessage?: string | null; // Error message to display above the table
  /** If set, this table section is rendered right after the form section with this key (e.g. channel_connection_info). */
  insertAfterSection?: string;
  // Search form support
  searchFormConfig?: any[]; // FormFieldConfig[] from SearchFormComponent
  primaryButton?: StatusActionButton;
  onSearch?: (searchData: any) => void;
  onReset?: () => void;
  onprimaryButtonClick?: () => void;
  onPageChange?: (event: any) => void;
  // Advanced table support
  useAdvancedTable?: boolean;
  defaultColumns?: TableColumn[];
  flexibleColumns?: TableColumn[];
  onColumnConfigClick?: (event: Event) => void;
  /** When true, row action links (e.g. Edit, View, Delete) are disabled. Used when channel status is inactive. */
  disableRowActions?: boolean;
  /** When false, row actions (e.g. Edit, Delete) are hidden. Used for read-only mapping tables. */
  allowRowActions?: boolean;
  /** When false, paginator is hidden and all rows are shown (use with rows >= data.length). */
  showPaginator?: boolean;
  /** Options for "Add" dropdown (e.g. optional SKU fields). When selected, onAddDropdownSelect is called. */
  addDropdownOptions?: { label: string; value: string }[];
  addDropdownPlaceholder?: string;
  onAddDropdownSelect?: (value: string) => void;
  /** Called when the add-dropdown menu overlay hides; use to refresh section (e.g. update table) after selection so 1 click applies. */
  onAddDropdownMenuHide?: () => void;
}

export interface TableAction {
  label: string;
  action: string;
  severity?: ButtonSeverity;
  outlined?: boolean;
  disabled?: boolean;
  icon?: string;
}

export interface ActionButton {
  label: string;
  action: string;
  severity?: ButtonSeverity;
  outlined?: boolean;
  disabled?: boolean;
  loading?: boolean;
  icon?: string;
}

export interface SectionButton {
  label: string;
  action: string;
  severity?: ButtonSeverity;
  outlined?: boolean;
  disabled?: boolean;
  icon?: string;
  section: string;
}

export interface SummarySection {
  title: string;
  show: boolean;
  items: SummaryItem[];
}

export interface SummaryItem {
  label: string;
  prop?: string;
  value: number | string;
  formatter?: 'currency' | 'number' | 'text';
  currency?: string;
}

export interface FileUploadData {
  file: File | null;
  previewUrl: string;
  fileName: string;
  fileSize: number;
  isExisting?: boolean;
}

export interface ScopeLineItem {
  id: string;
  scopeType: string;
  scopeValue: string;
}

export interface MultibuyTierItem {
  id: string;
  buyQty: number | null;
  getQty: number | null;
  atPercent: number | null;
}

@Component({
  selector: 'app-add-form',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    ButtonModule,
    SelectModule,
    InputTextModule,
    TextareaModule,
    RadioButtonModule,
    TableModule,
    CheckboxModule,
    DatePickerModule,
    InputNumberModule,
    DividerModule,
    DialogModule,
    DragDropModule,
    DataTableComponent,
    AdvancedDataTableComponent,
    FileUploadComponent,
    MultiFileUploadComponent,
    HtmlDescriptionComponent,
    GalleriaComponent,
    TranslateModule,
    TreeSelectModule,
    AutoCompleteModule,
    MultiSelectModule,
    TagModule,
    TooltipModule,
    MenuModule,
    AppButtonComponent,
    AdvancedDatepickerComponent,
    SearchFormComponent,
    StatusFilterComponent,
    UnicodeNormalizeDirective,
    SpuFaqEditorComponent,
  ],
  templateUrl: './add-form.component.html',
  styleUrl: './add-form.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddFormComponent implements OnInit, OnDestroy {
  private readonly fb = inject(FormBuilder);
  private readonly translateService = inject(TranslateService);
  private readonly messageService = inject(MessageService, { optional: true });
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly textNormalizer = inject(TextNormalizerService);
  private readonly destroy$ = new Subject<void>();

  // Track IME composition state per field to avoid normalizing during composition
  private readonly compositionState = new Map<string, boolean>();

  /** Public so parent (e.g. product-listing-form) can trigger re-render after setting image controls */
  public markForCheck(): void {
    this.cdr.markForCheck();
  }

  // Currency formatter method
  formatCurrency = formatCurrencyVND;

  // Computed signals for galleria images
  private readonly _detailedImagesSignal = signal<GalleriaImage[]>([]);
  private _lastDetailedImageValue: any = null;
  private readonly _mainImagesSignal = signal<GalleriaImage[]>([]);
  private _lastMainImageValue: any = null;

  // Track last emitted form value to prevent unnecessary emissions
  private _lastEmittedFormValue: string | null = null;

  @ContentChild('actionsTemplate') actionsTemplate!: TemplateRef<any>;
  @ContentChild('propertyAssetTemplate')
  propertyAssetTemplate!: TemplateRef<any>;
  @ContentChild('customTemplate') customTemplate!: TemplateRef<any>;

  // Access to multi-file-upload components for submission data
  @ViewChildren('multiFileUpload') multiFileUploadComponents?: QueryList<any>;

  // Access to advanced data table components for column configuration
  @ViewChildren(AdvancedDataTableComponent)
  advancedTables?: QueryList<AdvancedDataTableComponent>;
  @Input() height: string = '100%';
  @Input() title = 'Add Form';
  @Input() editMode = false;
  @Input() enableOverflow: boolean = true; // Default to true for backward compatibility
  @Input() uploadConfig: any = null; // Upload detail config for i18n placeholders
  @Input() set viewMode(value: boolean) {
    this._viewMode = value;
    this.handleViewModeChange();
  }
  get viewMode(): boolean {
    return this._viewMode;
  }
  private _viewMode = false;
  @Input() taxValidationLoading: boolean = false;
  @Input() textOnlyMode = false; // Only for marketplace order view - display fields as text instead of disabled inputs
  private readonly modifiedControls = new Set<string>(); // Track which controls have been modified by the user
  private removedImages: string[] = []; // Track removed detail image IDs for submission
  private removedMainImages: string[] = []; // Track removed main image IDs for submission

  /** Toggle password visibility per controlName (eye icon). */
  passwordVisible = signal<Record<string, boolean>>({});

  togglePasswordVisible(controlName: string): void {
    this.passwordVisible.update((prev) => ({
      ...prev,
      [controlName]: !prev[controlName],
    }));
  }

  getPasswordInputType(controlName: string): 'password' | 'text' {
    return this.passwordVisible()[controlName] ? 'text' : 'password';
  }

  isPasswordToggleVisible(field: FormField): boolean {
    return !field.readonly || field.passwordToggleWhenReadonly === true;
  }

  isPasswordInputDisabled(field: FormField): boolean {
    if (field.passwordReadonlyOnly) {
      return !!field.disabled;
    }
    return !!(field.disabled || this.isFieldDisabled(field) || this.viewMode);
  }

  copyPasswordFieldValue(field: FormField): void {
    const raw = this.form.get(field.controlName)?.value;
    const text = raw == null ? '' : String(raw);
    if (!text) {
      return;
    }
    navigator.clipboard
      .writeText(text)
      .then(() => {
        this.messageService?.add({
          severity: 'success',
          summary: this.translateService.instant('common.messages.success'),
          detail: this.translateService.instant(
            'common.messages.copied_to_clipboard',
          ),
        });
      })
      .catch(() => {
        this.messageService?.add({
          severity: 'error',
          summary: this.translateService.instant('common.messages.error'),
          detail: this.translateService.instant('common.messages.copy_failed'),
        });
      });
  }

  // Reactive existing files for multi-file components
  private readonly existingFilesCache = new Map<string, any[]>();
  private _previousInitialData: any = null;

  @Input() set initialData(data: any) {
    const previousHadData = this.hasKeyFieldData(this._previousInitialData);
    const incomingHasData = this.hasKeyFieldData(data);
    if (
      previousHadData &&
      !incomingHasData &&
      (this.editMode || this.viewMode)
    ) {
      return;
    }

    this._previousInitialData = this._initialData;
    this._initialData = data;

    this.preserveAndRestoreImageCache(data);

    const fields = this.formFieldsSignal();
    if (data && fields.length > 0) {
      this.populateExistingFilesCache(data, fields);
    }

    if (this.form && data) {
      this.patchExistingForm(data);
    } else if (data && this.formFieldsSignal().length > 0) {
      this.initNewFormFromData(data);
    }
  }
  get initialData(): any {
    return this._initialData;
  }
  private _initialData: any = null;
  @Input() set formFields(fields: FormField[]) {
    const previousFields = this.formFieldsSignal();
    this.syncPaginatedControllers(fields);
    this.formFieldsSignal.set(fields);

    if (this._initialData && fields.length > 0) {
      this.populateExistingFilesCache(this._initialData, fields);
    }

    if (this.form) {
      const shapeChanged = this.hasFieldShapeChanged(previousFields, fields);
      const optionsChanged =
        !shapeChanged && this.hasFieldOptionsChanged(previousFields, fields);

      if (shapeChanged) {
        this.rebuildFormPreservingValues();
      } else if (optionsChanged) {
        this.formFieldsSignal.set([...fields]);
        this.updateFieldDisabledStates();
      } else {
        this.updateFieldDisabledStates();
      }
      this.cdr.markForCheck();
    } else if (fields && fields.length > 0) {
      this.initForm();
    }
  }
  @Input() set tableSections(sections: TableSection[]) {
    this.tableSectionsSignal.set(sections || []);
    // Trigger change detection for OnPush strategy
    // This ensures error messages in table sections are displayed immediately
    this.cdr.markForCheck();
  }
  @Input() set summarySection(section: SummarySection | null) {
    this.summarySectionSignal.set(section);
  }
  @Input() set loading(value: boolean) {
    this.loadingSignal.set(value);
  }
  @Input() showScopeLinesInPeriodicSection = false;
  @Input() scopeLines: ScopeLineItem[] = [];
  @Input() scopeTypeOptions: { label: string; value: string }[] = [];
  @Input() showScopeLinesInMixMatchSection = false;
  @Input() mixMatchScopeLines: ScopeLineItem[] = [];
  @Input() mixMatchScopeTypeOptions: { label: string; value: string }[] = [];
  @Input() showMultibuyTiersInSection = false;
  @Input() multibuyTiers: MultibuyTierItem[] = [];
  /** Injected after scalar fields in this section (e.g. offer schema array tables). */
  @Input() configurationAppendTemplate: TemplateRef<unknown> | null = null;
  @Input() configurationAppendSectionKey = 'offer.form.sections.configuration';
  /** SPU FAQ editor, rendered as the last block in the form (opt-in, off by default). */
  @Input() showSpuFaq = false;
  @Input() spuFaqItems: FaqEditItem[] = [];
  @Output() spuFaqItemsChange = new EventEmitter<FaqEditItem[]>();

  @Output() scopeLineAdd = new EventEmitter<void>();
  @Output() scopeLineDelete = new EventEmitter<string>();
  @Output() scopeLineTypeChange = new EventEmitter<{
    id: string;
    scopeType: string;
  }>();
  @Output() scopeLineValueChange = new EventEmitter<{
    id: string;
    scopeValue: string;
  }>();
  @Output() mixMatchScopeLineAdd = new EventEmitter<void>();
  @Output() mixMatchScopeLineDelete = new EventEmitter<string>();
  @Output() mixMatchScopeLineTypeChange = new EventEmitter<{
    id: string;
    scopeType: string;
  }>();
  @Output() mixMatchScopeLineValueChange = new EventEmitter<{
    id: string;
    scopeValue: string;
  }>();
  @Output() multibuyTierAdd = new EventEmitter<void>();
  @Output() multibuyTierDelete = new EventEmitter<string>();
  @Output() multibuyTierBuyQtyChange = new EventEmitter<{
    id: string;
    buyQty: number | null;
  }>();
  @Output() multibuyTierGetQtyChange = new EventEmitter<{
    id: string;
    getQty: number | null;
  }>();
  @Output() multibuyTierAtPercentChange = new EventEmitter<{
    id: string;
    atPercent: number | null;
  }>();
  get loading() {
    return this.loadingSignal();
  }
  private readonly loadingSignal = signal(false);
  @Input() headerActions: ActionButton[] = [
    {
      label: 'common.actions.cancel',
      action: 'cancel',
      outlined: true,
      severity: 'secondary',
    },
    { label: 'common.actions.save', action: 'save', severity: 'info' },
  ];
  @Input() sectionButtons: SectionButton[] = [];
  @Input() scrollHeight: string = '400px';
  @Input() getFieldError?: (
    rowIndex: number,
    fieldName: string,
  ) => string | null;

  // Computed header actions based on view mode
  computedHeaderActions = computed(() => {
    if (this.viewMode) {
      // Check if custom header actions are provided (different from default)
      const defaultActions = [
        {
          label: 'common.actions.cancel',
          action: 'cancel',
          outlined: true,
          severity: 'secondary',
        },
        { label: 'common.actions.save', action: 'save', severity: 'info' },
      ];

      // If headerActions are different from default, use them; otherwise use default view mode action
      const isCustomActions = this.headerActions.some(
        (action) =>
          !defaultActions.some(
            (defaultAction) =>
              defaultAction.action === action.action &&
              defaultAction.label === action.label,
          ),
      );

      return isCustomActions
        ? this.headerActions
        : [
            {
              label: 'common.actions.go_back',
              action: 'cancel',
              outlined: true,
              severity: 'secondary' as const,
            },
          ];
    }

    // Apply loading state to save actions when component is loading
    return this.headerActions.map((action) => {
      if (action.action === 'save' && this.loadingSignal()) {
        return {
          ...action,
          loading: true,
          disabled: true,
        };
      }
      return action;
    });
  });

  // Output events
  @Output() formAction = new EventEmitter<{ action: string; data?: any }>();
  @Output() tableAction = new EventEmitter<{
    section: string;
    action: string;
    data?: any;
  }>();
  @Output() sectionAction = new EventEmitter<{
    section: string;
    action: string;
    data?: any;
  }>();
  @Output() formValueChanges = new EventEmitter<any>();
  @Output() imageRemoved = new EventEmitter<{
    fieldName: string;
    fileName: string;
    index?: number;
  }>();
  @Output() fileChanged = new EventEmitter<{
    fieldName: string;
    fileData: any;
  }>();
  @Output() existingImagesReordered = new EventEmitter<{
    fieldName: string;
    files: any[];
  }>();
  @Output() warehouseSearch = new EventEmitter<{
    query: string;
    field: string;
  }>();
  @Output() warehouseSelect = new EventEmitter<{
    selected: any;
    field: string;
  }>();
  @Output() couponSearch = new EventEmitter<{
    query: string;
    field: string;
  }>();
  @Output() couponSelect = new EventEmitter<{
    selected: any;
    field: string;
  }>();
  @Output() couponUnselect = new EventEmitter<{
    removed: any;
    field: string;
  }>();
  @Output() taxValidationClick = new EventEmitter<void>();

  // Signals for reactive state management
  formFieldsSignal = signal<FormField[]>([]);
  private readonly paginatedControllers = new Map<
    string,
    PaginatedDropdownController
  >();
  tableSectionsSignal = signal<TableSection[]>([]);
  summarySectionSignal = signal<SummarySection | null>(null);

  // Computed values
  sections = computed(() => {
    const sectionSet = new Set<string>();
    this.formFieldsSignal().forEach((field) => {
      if (!field.hidden) sectionSet.add(field.section);
    });
    const appendKey = this.configurationAppendSectionKey;
    if (this.configurationAppendTemplate && appendKey) {
      if (!sectionSet.has(appendKey)) {
        sectionSet.add(appendKey);
      }
    }
    return Array.from(sectionSet);
  });

  form!: FormGroup;

  /** Per-section selected value for "Add" dropdown (reset after select to allow adding same again) */
  addDropdownSelectedValueMap: Record<string, string | null> = {};

  /** Menu for "Add field" (e.g. optional SKU fields) – opened from Thêm button, same style as Tạo Kho hàng */
  @ViewChild('addFieldMenu') addFieldMenu!: Menu;
  currentAddSection: TableSection | null = null;
  /** Model cache khi mở menu – tránh [model] đổi giữa chừng làm mất click (chọn 1 lần là apply). */
  cachedAddFieldMenuItems: MenuItem[] = [];

  onAddDropdownSelect(tableSection: TableSection, value: string | null): void {
    if (value && tableSection.onAddDropdownSelect) {
      tableSection.onAddDropdownSelect(value);
      const key = tableSection.name || tableSection.title;
      this.addDropdownSelectedValueMap = {
        ...this.addDropdownSelectedValueMap,
        [key]: null,
      };
    }
  }

  openAddFieldMenu(section: TableSection, event: Event): void {
    this.currentAddSection = section;
    this.cachedAddFieldMenuItems = this.buildAddFieldMenuItems(section);
    this.cdr.markForCheck();
    const btn =
      (event?.currentTarget as HTMLElement) ||
      (event?.target as HTMLElement)?.closest?.('button');
    const eventForMenu = btn
      ? ({
          currentTarget: btn,
          target: event?.target,
          preventDefault: () => {},
          stopPropagation: () => {},
        } as unknown as Event)
      : event;
    setTimeout(() => this.addFieldMenu?.toggle(eventForMenu), 0);
  }

  /** Gọi khi menu "Thêm" đóng – refresh section (table + dropdown options). */
  onAddFieldMenuHide(): void {
    this.currentAddSection?.onAddDropdownMenuHide?.();
  }

  /** Build menu items từ section (dùng khi mở menu để cache). */
  private buildAddFieldMenuItems(section: TableSection): MenuItem[] {
    if (!section?.addDropdownOptions?.length) return [];
    return section.addDropdownOptions.map((opt) => ({
      label: opt.label,
      command: () => {
        section.onAddDropdownSelect?.(opt.value);
      },
    }));
  }

  getAddFieldMenuItems(): MenuItem[] {
    return this.cachedAddFieldMenuItems;
  }

  booleanOptions = [
    { label: 'common.options.no', value: false },
    { label: 'common.options.yes', value: true },
  ];

  selectedRadioValues: { [key: string]: any } = {};
  additionalValues: { [key: string]: any } = {};
  dropdownSelectionState: { [key: string]: boolean } = {};

  ngOnInit(): void {
    this.initForm();
    this.initializeRadioValues();
    this.setupFormValueChanges();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.destroyPaginatedControllers();

    // Clear signals
    this._detailedImagesSignal.set([]);
    this._mainImagesSignal.set([]);
  }

  private setupFormValueChanges(): void {
    if (this.form) {
      this.form.valueChanges
        .pipe(
          takeUntil(this.destroy$),
          debounceTime(100),
          distinctUntilChanged((prev, curr) => {
            // Safe comparison to prevent circular reference issues
            return this.isFormValueEqual(prev, curr);
          }),
        )
        .subscribe((value) => {
          // Update field disabled states when form values change
          this.updateFieldDisabledStates();

          const formValue = {
            ...value,
            selectedRadioValues: this.selectedRadioValues,
            additionalValues: this.additionalValues,
          };

          // Update detailed images signal when form values change
          this.updateDetailedImages();

          // Update main images signal when form values change
          this.updateMainImages();

          this.formValueChanges.emit(formValue);

          // Trigger change detection for dynamic labels
          this.updateDynamicLabels();
        });
    }
  }

  private updateDynamicLabels(): void {
    // Force change detection for fields with dynamic labels
    const fieldsWithDynamicLabels = this.formFieldsSignal().filter(
      (field) => field.dynamicLabel,
    );
    if (fieldsWithDynamicLabels.length > 0) {
      // Trigger change detection by updating the form fields signal
      this.formFieldsSignal.set([...this.formFieldsSignal()]);
    }
  }

  private initializeRadioValues(): void {
    this.formFieldsSignal().forEach((field) => {
      if (field.type === 'radio') {
        if (this.selectedRadioValues[field.controlName] === undefined) {
          this.selectedRadioValues[field.controlName] = false;
        }
      }
    });
  }

  private initForm(): void {
    const group: any = {};
    this.formFieldsSignal().forEach((field) =>
      this.registerFieldControl(group, field),
    );

    if (this.form) {
      const oldValue = this.form.value;
      this.form = this.fb.group(group);
      this.form.patchValue(oldValue);
    } else {
      this.form = this.fb.group(group);
    }

    // Handle view mode after form is created
    this.handleViewModeChange();

    // Disable fields that are marked as disabled in configuration
    this.initializeDisabledFields();

    // Handle conditional field initialization only if we don't have initial data
    // (initial data will trigger its own conditional field initialization)
    if (!this.initialData) {
      this.initializeConditionalFields();
    }

    this.setupFormValueChanges();
  }

  private handleViewModeChange(): void {
    if (this.form) {
      if (this._viewMode) {
        this.form.disable();
      } else {
        this.form.enable();
      }
    }
  }

  getFieldsBySection(section: string): FormField[] {
    return this.formFieldsSignal()
      .filter((field) => field.section === section && !field.nestedUnder)
      .map((field) => this.enrichPaginatedField(field));
  }

  /** Fields nested beneath `parentControlName`, inside its own grid cell (see FormField.nestedUnder). */
  getNestedFields(section: string, parentControlName: string): FormField[] {
    return this.formFieldsSignal().filter(
      (field) =>
        field.section === section && field.nestedUnder === parentControlName,
    );
  }

  enrichPaginatedField(field: FormField): FormField {
    const controller = this.paginatedControllers.get(field.controlName);
    if (!controller) {
      return field;
    }

    const paginatedProps = controller.toSearchableDropdownProps();
    const paginatedOptions = paginatedProps.options ?? [];
    const staticOptions = field.options ?? [];
    const options =
      paginatedOptions.length > 0
        ? paginatedOptions
        : staticOptions.length > 0
          ? staticOptions
          : paginatedOptions;

    return {
      ...field,
      type: 'searchableDropdown',
      ...paginatedProps,
      options,
    };
  }

  private syncPaginatedControllers(fields: FormField[]): void {
    const activeKeys = new Set<string>();

    for (const field of fields) {
      if (!field.paginatedOptions) {
        continue;
      }

      activeKeys.add(field.controlName);

      if (!this.paginatedControllers.has(field.controlName)) {
        const controller = new PaginatedDropdownController({
          ...field.paginatedOptions,
          getCurrentValue: () => this.form?.get(field.controlName)?.value,
          onOptionsChange: () => this.cdr.markForCheck(),
        });
        this.paginatedControllers.set(field.controlName, controller);
        controller.load({ reset: true });
      }
    }

    for (const [key, controller] of this.paginatedControllers) {
      if (!activeKeys.has(key)) {
        controller.destroy();
        this.paginatedControllers.delete(key);
      }
    }
  }

  private destroyPaginatedControllers(): void {
    for (const controller of this.paginatedControllers.values()) {
      controller.destroy();
    }
    this.paginatedControllers.clear();
  }

  /** Reload paginated dropdown options (e.g. after parent filter criteria change). */
  reloadPaginatedField(controlName: string): void {
    const controller = this.paginatedControllers.get(controlName);
    if (controller) {
      controller.load({ reset: true });
      this.cdr.markForCheck();
    }
  }

  getSectionGridColsClass(section: string): string {
    const cols = this.getFieldsBySection(section).find(
      (f) => f.sectionGridCols,
    )?.sectionGridCols;
    return cols ? `grid-cols-${cols}` : 'grid-cols-3';
  }

  getSectionButtons(section: string): SectionButton[] {
    return this.sectionButtons.filter((button) => button.section === section);
  }

  /** Table sections that should be rendered right after the given form section key. */
  getTableSectionsAfter(sectionKey: string): TableSection[] {
    return (this.tableSectionsSignal() ?? []).filter(
      (s) => s.insertAfterSection === sectionKey,
    );
  }

  /** Table sections at end: no insertAfterSection, or insertAfterSection target not in sections() (so section still shows). */
  getTableSectionsAtEnd(): TableSection[] {
    const sectionKeys = this.sections();
    return (this.tableSectionsSignal() ?? []).filter(
      (s) =>
        !s.insertAfterSection || !sectionKeys.includes(s.insertAfterSection),
    );
  }

  normalizeTestIdSegment(value: any): string {
    return (
      String(value ?? "item")
        .replace(/([a-z0-9])([A-Z])/g, "$1-$2")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "") || "item"
    );
  }

  getTableSectionTestId(tableSection: TableSection, suffix = "table"): string {
    const sectionKey =
      tableSection.testIdKey ||
      tableSection.name ||
      tableSection.titleKey ||
      tableSection.insertAfterSection ||
      tableSection.title ||
      "section";

    return `add-form-${this.normalizeTestIdSegment(sectionKey)}-${this.normalizeTestIdSegment(suffix)}`;
  }

  getSectionButtonTestId(section: string, action: string): string {
    return `add-form-section-${this.normalizeTestIdSegment(section)}-action-${this.normalizeTestIdSegment(action)}`;
  }

  getFieldActionTestId(field: FormField, action: string): string {
    return `add-form-${this.normalizeTestIdSegment(field.controlName)}-${this.normalizeTestIdSegment(action)}-btn`;
  }

  getTableRowActionTestId(
    tableSection: TableSection,
    rowData: any,
    action: string,
  ): string {
    const rowKey =
      rowData?.id ??
      rowData?.code ??
      rowData?.skuCode ??
      rowData?.spuCode ??
      rowData?.skuId ??
      rowData?.spuId ??
      rowData?.itemId ??
      rowData?.productId ??
      rowData?.name ??
      rowData?.title ??
      "row";

    return `${this.getTableSectionTestId(tableSection, `action-${action}`)}-${this.normalizeTestIdSegment(rowKey)}`;
  }

  onRadioChange(fieldName: string, value: any) {
    this.selectedRadioValues[fieldName] = value;
    const control = this.form.get(fieldName);
    if (control) control.setValue(value);
    this.emitFormValues();
  }

  onDropdownChange(fieldName: string, event: any) {
    const control = this.form.get(fieldName);
    if (control) {
      // Track that this dropdown has been interacted with
      this.dropdownSelectionState[fieldName] = true;

      // Check if this is a multiselect field
      const field = this.formFieldsSignal().find(
        (f) => f.controlName === fieldName,
      );
      const isMultiselect = field?.multiple;

      if (isMultiselect) {
        // For multiselect: set value without emitting events
        // Events will be emitted when dropdown closes
        control.setValue(event.value, { emitEvent: false });
      } else {
        // For single select: set value and emit events immediately
        control.setValue(event.value, { emitEvent: true });
      }

      // Mark as dirty/touched if there's actually a value selected
      // Only mark as touched when a value is selected, not during selection
      if (
        event.value !== null &&
        event.value !== undefined &&
        event.value !== ''
      ) {
        control.markAsDirty();
        // Delay marking as touched to avoid showing validation during selection
        setTimeout(() => {
          if (control.value === event.value) {
            control.markAsTouched();
          }
        }, 100);
      }

      // Trigger validation but don't show errors until dropdown is closed
      // For fields with dependsOn, delay validation to avoid form jumping
      const hasDependentFields = this.formFieldsSignal().some(
        (f) => f.dependsOn === fieldName,
      );
      if (hasDependentFields) {
        // Delay validation for parent fields to avoid form jumping
        setTimeout(() => {
          control.updateValueAndValidity({ emitEvent: !isMultiselect });
        }, 150);
      } else {
        control.updateValueAndValidity({ emitEvent: !isMultiselect });
      }

      if (!isMultiselect) {
        // For single select, emit form values immediately
        this.emitFormValues();
      }
      // For multiselect, don't emit yet - wait for onClose

      // Handle conditional field enabling/disabling for dropdown changes
      this.handleConditionalFields(fieldName, event.value);
      // Update all field disabled states after conditional field changes
      this.updateFieldDisabledStates();

      // Trigger dynamic label updates when dropdown values change
      this.updateDynamicLabels();
    } else {
      this.emitFormValues();
    }
  }

  onMultiselectClose(fieldName: string): void {
    const control = this.form.get(fieldName);
    if (control) {
      // Update validity and emit form values when multiselect dropdown closes
      control.updateValueAndValidity({ emitEvent: true });
    }
    // Emit form values when multiselect dropdown closes
    this.emitFormValues();
  }

  onTextInputChange(fieldName: string, event: any) {
    const control = this.form.get(fieldName);
    if (!control) return;
    const field = this.formFieldsSignal().find(
      (f) => f.controlName === fieldName,
    );
    if (field && this.hasPhoneValidator(field)) {
      this.handlePhoneFieldInput(fieldName, event, field, control);
    } else {
      this.handleNonPhoneFieldInput(fieldName, event, field, control);
    }
  }

  onCompositionStart(fieldName: string): void {
    // Mark that IME composition has started for this field
    this.compositionState.set(fieldName, true);
  }

  onCompositionEnd(fieldName: string, event: CompositionEvent): void {
    // Mark that IME composition has ended
    this.compositionState.set(fieldName, false);

    // Always normalize after composition ends to ensure proper NFC form
    // This fixes issues where NFD characters display incorrectly (e.g., "Nhâ.p" instead of "Nhập")
    this.normalizeFieldValue(fieldName, event.target as HTMLInputElement);
  }

  onTextBlur(fieldName: string, event: FocusEvent): void {
    const control = this.form.get(fieldName);
    const field = this.formFieldsSignal().find(
      (f) => f.controlName === fieldName,
    );

    // For phone fields, always validate on blur
    if (control && field && this.hasPhoneValidator(field)) {
      control.markAsTouched();
      control.updateValueAndValidity({ emitEvent: true });
    }

    // Normalize on blur as a fallback to ensure proper NFC form
    // This catches any cases where composition end might have been missed
    const inputElement = event.target as HTMLInputElement;
    this.normalizeFieldValue(fieldName, inputElement);
  }

  /**
   * Check if a field has a phone number validator
   */
  private hasPhoneValidator(field: FormField): boolean {
    if (!field.validators || field.validators.length === 0) {
      return false;
    }
    return field.validators.some((validator) => {
      if (typeof validator === 'function') {
        const validatorStr = validator.toString();
        return (
          validatorStr.includes('phoneNumberValidator') ||
          validatorStr.includes('validatePhoneNumber')
        );
      }
      return false;
    });
  }

  /**
   * Check if phone number has reached exact length for validation
   * - Starts with '0': validate at exactly 10 digits
   * - Starts with '+84': validate at 11 or 12 digits
   */
  private shouldValidatePhone(value: string, cleanedValue: string): boolean {
    // Check for +84 prefix (international format)
    if (value.startsWith('+84')) {
      return cleanedValue.length === 11 || cleanedValue.length === 12;
    }
    // Check for 0 prefix (local format)
    if (value.startsWith('0')) {
      return cleanedValue.length === 10;
    }
    // Other formats - don't auto-validate on input (will validate on blur)
    return false;
  }

  private normalizeFieldValue(
    fieldName: string,
    inputElement: HTMLInputElement,
  ): void {
    const control = this.form.get(fieldName);
    if (!control || !inputElement) {
      return;
    }

    const field = this.formFieldsSignal().find(
      (f) => f.controlName === fieldName,
    );

    // Check if field should be normalized (has Vietnamese validator)
    const shouldNormalize = field?.validators?.some((validator) => {
      if (typeof validator === 'function') {
        const validatorStr = validator.toString();
        return (
          validatorStr.includes('normalizeToNFC') ||
          validatorStr.includes('vietnameseTextPatternValidator')
        );
      }
      return false;
    });

    if (shouldNormalize) {
      const value = inputElement.value || control.value || '';

      // Always normalize to NFC to ensure proper visual display
      // This converts NFD (decomposed) to NFC (composed) form
      // Even if strings compare as equal, they might be in different normalization forms
      const normalized = this.textNormalizer.normalizeToNFC(value, false);

      // Check if normalization is needed by comparing normalization forms
      // Sometimes NFD and NFC compare as equal but render differently
      const currentNormalized = value.normalize('NFC');
      const needsUpdate = normalized !== value || currentNormalized !== value;

      if (needsUpdate) {
        const cursorPosition = inputElement.selectionStart || normalized.length;

        // Update form control first (this will update the input via form binding)
        control.setValue(normalized, { emitEvent: true });

        // Also update input element directly to ensure immediate visual update
        // Use requestAnimationFrame to ensure DOM is ready
        requestAnimationFrame(() => {
          // Force update the input element value to ensure proper rendering
          inputElement.value = normalized;

          // Restore cursor position
          const newCursorPosition = Math.min(cursorPosition, normalized.length);
          inputElement.setSelectionRange(newCursorPosition, newCursorPosition);

          // Trigger change detection
          this.cdr.markForCheck();
        });
      }
    }
  }

  onCheckboxChange(fieldName: string, event: any) {
    const control = this.form.get(fieldName);
    if (control) {
      control.markAsDirty();
      control.markAsTouched();
      this.emitFormValues();

      // Handle conditional field enabling/disabling
      const value = event.checked;
      this.handleConditionalFields(fieldName, value);
      // Update all field disabled states after conditional field changes
      this.updateFieldDisabledStates();
    } else {
      this.emitFormValues();
    }
  }

  onDateSelect(fieldName: string, selectedDate: Date | DateRange | null) {
    const control = this.form.get(fieldName);
    if (control) {
      // Update the form control value with the selected Date object
      control.setValue(selectedDate, { emitEvent: true });
      control.markAsDirty();
      control.markAsTouched();

      // Trigger validation
      control.updateValueAndValidity({ emitEvent: true });

      // Explicitly emit form values to ensure parent component gets the update
      this.emitFormValues();
    }
  }

  onDateRangeSelect(fieldName: string, selectedRange: DateRange | Date | null) {
    const control = this.form.get(fieldName);
    if (control) {
      // Update the form control value with the selected DateRange object
      control.setValue(selectedRange, { emitEvent: true });
      control.markAsDirty();
      control.markAsTouched();

      // Trigger validation
      control.updateValueAndValidity({ emitEvent: true });

      // Explicitly emit form values to ensure parent component gets the update
      this.emitFormValues();
    }
  }

  private handleConditionalFields(changedFieldName: string, value: any) {
    let hasChanges = false;

    this.formFieldsSignal().forEach((field) => {
      if (field.dependsOn !== changedFieldName) return;
      const dependentControl = this.form.get(field.controlName);
      if (dependentControl) {
        hasChanges =
          this.applyConditionalFieldRule(field, dependentControl, value) ||
          hasChanges;
      }
    });

    this.updateFieldDisabledStates();

    if (hasChanges) {
      this.formFieldsSignal.set([...this.formFieldsSignal()]);
      this.updateDynamicLabels();
    }
  }

  private initializeDisabledFields(): void {
    // Update all field disabled states programmatically
    this.updateFieldDisabledStates();
  }

  private updateFieldDisabledStates(): void {
    if (!this.form) return;

    this.formFieldsSignal().forEach((field) => {
      const control = this.form.get(field.controlName);
      if (!control) return;

      if (field.passwordReadonlyOnly) {
        if (control.disabled) {
          control.enable({ emitEvent: false });
        }
        return;
      }

      const shouldBeDisabled = this.isFieldDisabled(field);
      const isCurrentlyDisabled = control.disabled;

      if (shouldBeDisabled && !isCurrentlyDisabled) {
        control.disable({ emitEvent: false });
      } else if (!shouldBeDisabled && isCurrentlyDisabled && !this.viewMode) {
        control.enable({ emitEvent: false });
      }
    });
  }

  private initializeConditionalFields() {
    let hasChanges = false;

    this.formFieldsSignal().forEach((field) => {
      if (!field.dependsOn) return;
      const parentControl = this.form.get(field.dependsOn);
      const dependentControl = this.form.get(field.controlName);
      if (parentControl && dependentControl) {
        hasChanges =
          this.resolveInitialConditionalState(
            field,
            parentControl,
            dependentControl,
          ) || hasChanges;
      }
    });

    this.updateFieldDisabledStates();

    if (hasChanges) {
      this.formFieldsSignal.set([...this.formFieldsSignal()]);
      this.updateDynamicLabels();
    }
  }

  private emitFormValues(): void {
    const formValue = {
      ...this.form.getRawValue(), // Use getRawValue() to include disabled controls
      selectedRadioValues: this.selectedRadioValues,
      additionalValues: this.additionalValues,
    };

    // Check if the form value actually changed to prevent unnecessary emissions
    // Use a safe comparison that handles circular references
    const currentFormValue = this.safeStringify(formValue);
    if (this._lastEmittedFormValue === currentFormValue) {
      return; // Skip emission if form value hasn't changed
    }

    this._lastEmittedFormValue = currentFormValue;
    this.formValueChanges.emit(formValue);
  }

  // Method to update form control values from external source
  public updateFormControlValue(controlName: string, value: any): void {
    const control = this.form.get(controlName);
    if (control) {
      control.setValue(value, { emitEvent: false });
    }
  }

  // Method to reset form control validation
  public resetFormControlValidation(controlName: string): void {
    const control = this.form.get(controlName);
    if (control) {
      control.markAsUntouched();
      control.markAsPristine();
      control.updateValueAndValidity({ emitEvent: false });
    }
  }

  // Method to trigger validation on related fields (for date validation)
  public triggerRelatedFieldValidation(
    controlName: string,
    relatedFields: string[],
  ): void {
    const control = this.form.get(controlName);
    if (control) {
      // Trigger validation on the current field
      control.updateValueAndValidity({ emitEvent: false });

      // Trigger validation on related fields
      relatedFields.forEach((fieldName) => {
        const relatedControl = this.form.get(fieldName);
        if (relatedControl) {
          relatedControl.updateValueAndValidity({ emitEvent: false });
        }
      });
    }
  }

  getMinDate(field: FormField): Date | null {
    // If dynamicMinDate is true, always use today's date (for both add and edit)
    if (field.dynamicMinDate) {
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Set to start of day
      return today;
    }

    // For edit mode, allow past dates (no minDate restriction) unless minDate is explicitly set
    if (this.editMode && field.minDate === undefined) {
      return null;
    }

    // For add mode or when minDate is explicitly set, use the configured minDate or today
    // Check if minDate is explicitly set (including null) vs undefined
    if (field.minDate !== undefined) {
      if (field.minDate === null) {
        return null;
      }
      // Normalize Date objects to start of day to avoid time comparison issues
      if (field.minDate instanceof Date) {
        const normalizedDate = new Date(field.minDate);
        normalizedDate.setHours(0, 0, 0, 0);
        return normalizedDate;
      }
      return field.minDate;
    }
    // Default for add mode when minDate is not set - normalize to start of day
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  }

  getFieldOptions(field: FormField): any[] {
    field = this.enrichPaginatedField(field);
    if (!field.options) return [];

    if (field.translateLabel === false) {
      // eslint-disable-next-line deprecation/deprecation
      // eslint-disable-next-line deprecation/deprecation
      const lang = this.translateService.currentLang || 'en';
      const vi = lang.startsWith('vi');
      return field.options.map((option: any) => {
        const raw = vi && option.labelVi ? option.labelVi : option.label;
        return { ...option, label: raw };
      });
    }

    return (
      field?.options?.map((option) => ({
        ...option,
        label: option.label ? this.translateService.instant(option.label) : '',
      })) || []
    );
  }

  // Warehouse autocomplete methods
  warehouseSuggestions: { [key: string]: any[] } = {};
  warehouseSuggestionsSignal = signal<{ [key: string]: any[] }>({});

  // Coupon autocomplete methods
  couponSuggestions: { [key: string]: any[] } = {};

  getWarehouseOptions(field: FormField): any[] {
    // Return the warehouse suggestions for this field
    return this.warehouseSuggestionsSignal()[field.controlName] || [];
  }

  // Method to update warehouse suggestions
  updateWarehouseSuggestions(fieldName: string, suggestions: any[]): void {
    this.warehouseSuggestions[fieldName] = suggestions;
    this.cdr.detectChanges();
  }

  // Method to clear warehouse suggestions
  clearWarehouseSuggestions(fieldName: string): void {
    this.warehouseSuggestions[fieldName] = [];
    this.cdr.detectChanges();
  }

  // Method to update coupon suggestions
  updateCouponSuggestions(fieldName: string, suggestions: any[]): void {
    // Ensure a new array reference so p-autocomplete detects changes
    this.couponSuggestions[fieldName] = [...(suggestions || [])];
    // Trigger template refresh for OnPush
    this.formFieldsSignal.set([...this.formFieldsSignal()]);
    this.cdr.markForCheck();
  }

  // Method to clear coupon suggestions
  clearCouponSuggestions(fieldName: string): void {
    this.couponSuggestions[fieldName] = [];
    this.formFieldsSignal.set([...this.formFieldsSignal()]);
    this.cdr.markForCheck();
  }

  onCouponSearch(event: any, field: FormField): void {
    const query = (event?.query ?? '').toString();
    this.couponSearch.emit({ query, field: field.controlName });
  }

  onCouponSelect(event: any, field: FormField): void {
    this.couponSelect.emit({ selected: event, field: field.controlName });
  }

  onCouponUnselect(event: any, field: FormField): void {
    this.couponUnselect.emit({ removed: event, field: field.controlName });
  }

  onWarehouseSearch(event: any, field: FormField): void {
    // Emit event to parent component to handle warehouse search
    this.warehouseSearch.emit({
      query: event.query,
      field: field.controlName,
    });
  }

  onWarehouseSelect(event: any, field: FormField): void {
    // Emit event to parent component to handle warehouse selection
    this.warehouseSelect.emit({
      selected: event,
      field: field.controlName,
    });
  }

  onHeaderAction(action: string): void {
    // Check if any header action is in loading state
    const loadingAction = this.computedHeaderActions().find((a) => a.loading);
    if (loadingAction || this.loadingSignal()) {
      return;
    }

    if (action === 'save') {
      if (this.hasMaxLengthError()) {
        this.markFormGroupTouched();
        return;
      }
      // Always emit formAction for save, even if form is invalid
      // This allows parent component to handle validation and scrolling
      const formData = this.getFormDataForSubmission();
      this.formAction.emit({ action, data: formData });

      // If form is invalid, also mark as touched to show Angular validation errors
      if (this.form.invalid) {
        this.markFormGroupTouched();
      }
    } else {
      this.formAction.emit({ action });
    }
  }

  onSectionAction(section: string, action: string, event?: Event): void {
    this.sectionAction.emit({ section, action, data: { event } });
  }

  handleprimaryButtonClick(tableSection: TableSection): void {
    if (tableSection.onprimaryButtonClick) {
      try {
        tableSection.onprimaryButtonClick();
      } catch (error) {
        console.error(
          'Error calling onprimaryButtonClick:',
          error instanceof Error ? error.message : 'unknown error',
        );
      }
    } else {
      console.warn(
        'onprimaryButtonClick is not defined for section:',
        tableSection.name,
      );
    }
  }

  handleColumnConfigClick(tableSection: TableSection, event: Event): void {
    if (tableSection.onColumnConfigClick) {
      tableSection.onColumnConfigClick(event);
    } else if (this.advancedTables && tableSection.useAdvancedTable) {
      const sections = this.tableSectionsSignal();
      const sectionIndex = sections.findIndex(
        (s) => s.name === tableSection.name || s.title === tableSection.title,
      );
      if (sectionIndex >= 0 && this.advancedTables.length > sectionIndex) {
        const advancedTable = this.advancedTables.toArray()[sectionIndex];
        const target = event.target as HTMLElement;
        const buttonElement = target.closest('button') || target;
        if (advancedTable && buttonElement) {
          advancedTable.openColumnSelector(buttonElement);
        }
      }
    }
  }

  onTableAction(sectionTitle: string, action: string, data?: any): void {
    const event = { section: sectionTitle, action, data };
    this.tableAction.emit(event);
  }

  onTableSelectionChange(sectionTitle: string, selection: any[]): void {
    this.tableAction.emit({
      section: sectionTitle,
      action: 'selectionChange',
      data: selection,
    });
  }

  onScopeLineAdd(): void {
    this.scopeLineAdd.emit();
  }

  onScopeLineDelete(id: string): void {
    this.scopeLineDelete.emit(id);
  }

  onScopeLineTypeChange(id: string, scopeType: string): void {
    this.scopeLineTypeChange.emit({ id, scopeType });
  }

  onScopeLineValueChange(id: string, scopeValue: string): void {
    this.scopeLineValueChange.emit({ id, scopeValue });
  }

  onMixMatchScopeLineAdd(): void {
    this.mixMatchScopeLineAdd.emit();
  }

  onMixMatchScopeLineDelete(id: string): void {
    this.mixMatchScopeLineDelete.emit(id);
  }

  onMixMatchScopeLineTypeChange(id: string, scopeType: string): void {
    this.mixMatchScopeLineTypeChange.emit({ id, scopeType });
  }

  onMixMatchScopeLineValueChange(id: string, scopeValue: string): void {
    this.mixMatchScopeLineValueChange.emit({ id, scopeValue });
  }

  onMultibuyTierAdd(): void {
    this.multibuyTierAdd.emit();
  }

  onMultibuyTierDelete(id: string): void {
    this.multibuyTierDelete.emit(id);
  }

  onMultibuyTierBuyQtyChange(id: string, buyQty: number | null): void {
    this.multibuyTierBuyQtyChange.emit({ id, buyQty });
  }

  onMultibuyTierGetQtyChange(id: string, getQty: number | null): void {
    this.multibuyTierGetQtyChange.emit({ id, getQty });
  }

  onMultibuyTierAtPercentChange(id: string, atPercent: number | null): void {
    this.multibuyTierAtPercentChange.emit({ id, atPercent });
  }

  onFileChange(fieldName: string, fileData: any): void {
    this.emitFormValues();
  }

  // New method to handle image removal events from file upload components
  onImageRemoved(fieldName: string, fileName: string, index?: number): void {
    // Emit the removal event to parent component
    this.imageRemoved.emit({ fieldName, fileName, index });

    // Track removed image for submission based on field type
    if (fileName) {
      if (fieldName === 'mainImage') {
        if (!this.removedMainImages.includes(fileName)) {
          this.removedMainImages.push(fileName);
        }
      } else if (!this.removedImages.includes(fileName)) {
        this.removedImages.push(fileName);
      }
    }

    if (index === undefined) {
      // Single file upload
      this.form.get(fieldName)?.setValue(null);
      this.modifiedControls.add(fieldName); // Mark as modified
    } else {
      // Multiple file upload - remove specific indexed control
      const controlName = this.getFileControlName(fieldName, index);
      const control = this.form.get(controlName);
      if (control) {
        control.setValue(null);
        this.modifiedControls.add(controlName); // Mark as modified
      }
    }

    // Emit file change event to parent component with null data
    this.fileChanged.emit({ fieldName, fileData: null });

    // Emit form values to parent
    this.emitFormValues();
  }

  // New methods for multiple file upload
  getFileUploadSlots(fieldName: string, maxFiles: number): number[] {
    // For detail images, we use indexed controls (detailImage_0, detailImage_1, etc.)
    // Find ALL actual indices that have values (not just consecutive ones)
    const existingIndices: number[] = [];

    // Check all possible indices up to a reasonable limit
    for (let i = 0; i < 20; i++) {
      // Safety limit to prevent infinite loop
      const controlName = this.getFileControlName(fieldName, i);
      const control = this.form.get(controlName);
      if (control?.value) {
        existingIndices.push(i);
      }
    }

    // Sort the indices to maintain order
    existingIndices.sort((a, b) => a - b);

    // Add one empty slot if not at max
    if (existingIndices.length < maxFiles) {
      // Find the next available index for the empty slot
      let nextIndex = 0;
      while (existingIndices.includes(nextIndex)) {
        nextIndex++;
      }
      existingIndices.push(nextIndex);
    }

    return existingIndices;
  }

  getFileControlName(fieldName: string, index: number): string {
    return `${fieldName}_${index}`;
  }

  onFileUpload(fileData: any, fieldName: string, index?: number): void {
    if (index === undefined) {
      // Single file upload
      this.form.get(fieldName)?.setValue(fileData);
      this.modifiedControls.add(fieldName); // Mark as modified
    } else {
      // Multiple file upload
      const currentFiles = this.form.get(fieldName)?.value || [];
      if (fileData) {
        // Add or update file at specific index
        currentFiles[index] = fileData;
      } else {
        // Remove file at specific index
        currentFiles.splice(index, 1);
      }
      this.form.get(fieldName)?.setValue([...currentFiles]);
      this.modifiedControls.add(fieldName); // Mark as modified
    }

    // Emit file change event to parent component
    this.fileChanged.emit({ fieldName, fileData });

    // Emit form values to parent
    this.emitFormValues();
  }

  onMultipleFilesSelected(files: any[], fieldName: string): void {
    const maxFiles = this.getMaxFilesForField(fieldName);
    const existingFiles = this.getExistingFiles(fieldName);
    const currentFileCount = existingFiles.length;

    // Check if total files would exceed max limit
    const totalFilesAfterUpload = currentFileCount + files.length;
    if (totalFilesAfterUpload > maxFiles) {
      const allowedFiles = maxFiles - currentFileCount;
      if (allowedFiles <= 0) {
        this.messageService?.add({
          severity: 'warn',
          summary: this.translateService.instant(
            'common.toast.upload.limit_reached',
          ),
          detail: this.translateService.instant(
            'common.toast.upload.upload_limit_message',
            { maxFiles },
          ),
        });
        return;
      }

      // Limit files to what can be uploaded
      files = files.slice(0, allowedFiles);

      this.messageService?.add({
        severity: 'warn',
        summary: this.translateService.instant(
          'common.toast.upload.limit_exceeded',
        ),
        detail: this.translateService.instant(
          'common.toast.upload.upload_exceeded_message',
          {
            allowedFiles,
            totalFiles: files.length + (totalFilesAfterUpload - maxFiles),
            maxFiles,
          },
        ),
      });
    }

    // Check for duplicate files
    const duplicateFiles: string[] = [];
    const newFiles: any[] = [];

    files.forEach((fileData) => {
      const isDuplicate = existingFiles.some(
        (existingFile) =>
          existingFile.fileName === fileData.fileName &&
          existingFile.fileSize === fileData.fileSize,
      );

      if (isDuplicate) {
        duplicateFiles.push(fileData.fileName);
      } else {
        newFiles.push(fileData);
      }
    });

    // Show duplicate files warning
    if (duplicateFiles.length > 0) {
      this.messageService?.add({
        severity: 'warn',
        summary: this.translateService.instant(
          'common.toast.upload.duplicate_files',
        ),
        detail: this.translateService.instant(
          'common.toast.upload.duplicate_files_message',
          {
            fileNames: duplicateFiles.join(', '),
          },
        ),
      });
    }

    // Only process new files
    if (newFiles.length === 0) {
      return;
    }

    // Find the next available indices for the new files
    const existingIndices = this.getExistingIndices(fieldName);
    let nextIndex = 0;
    while (existingIndices.includes(nextIndex)) {
      nextIndex++;
    }

    // Add each new file to the next available index
    newFiles.forEach((fileData, i) => {
      const targetIndex = nextIndex + i;

      // Double-check we're within the max files limit
      if (targetIndex < maxFiles) {
        const controlName = this.getFileControlName(fieldName, targetIndex);
        this.form.get(controlName)?.setValue(fileData);
        this.modifiedControls.add(controlName);
      }
    });

    // Show success message for new files
    if (newFiles.length > 0) {
      this.messageService?.add({
        severity: 'success',
        summary: this.translateService.instant(
          'common.toast.upload.files_uploaded',
        ),
        detail: this.translateService.instant(
          'common.toast.upload.upload_success',
          {
            count: newFiles.length,
          },
        ),
      });
    }

    // Emit form values to parent
    this.emitFormValues();
  }

  private getExistingIndices(fieldName: string): number[] {
    const indices: number[] = [];
    for (let i = 0; i < 20; i++) {
      // Safety limit
      const controlName = this.getFileControlName(fieldName, i);
      if (this.form.get(controlName)?.value) {
        indices.push(i);
      }
    }
    return indices;
  }

  public getMaxFilesForField(fieldName: string): number {
    const field = this.formFieldsSignal().find(
      (f) => f.controlName === fieldName,
    );
    const maxFiles = (field as any)?.maxFiles || field?.maxFiles || 1;
    // Check both the field's maxFiles property and the dynamically set maxFiles from upload config
    return maxFiles;
  }

  public getExistingFiles(fieldName: string): any[] {
    const field = this.formFieldsSignal().find(
      (f) => f.controlName === fieldName,
    );

    if (
      field?.type === 'file' &&
      !Array.isArray(this.initialData?.[fieldName])
    ) {
      return this.getExistingSingleFile(fieldName);
    }

    if (
      field?.type === 'multi-file' ||
      (field?.type === 'file' && Array.isArray(this.initialData?.[fieldName]))
    ) {
      return this.getExistingMultiFiles(fieldName);
    }

    // Legacy indexed file fields
    const existingFiles: any[] = [];
    for (let i = 0; i < 20; i++) {
      const value = this.form.get(this.getFileControlName(fieldName, i))?.value;
      if (value) existingFiles.push(value);
    }
    return existingFiles;
  }

  /**
   * Populate existing files cache from initial data
   * This method is called when initialData is set or when formFields are set
   */
  private populateExistingFilesCache(data: any, fields: FormField[]): void {
    fields.forEach((field) => {
      if (!data[field.controlName]) {
        return;
      }

      const fieldData = data[field.controlName];

      // Handle single file field (mainImage) - expects object, not array
      if (
        field.type === 'file' &&
        !Array.isArray(fieldData) &&
        typeof fieldData === 'object'
      ) {
        const convertedFile = {
          id: fieldData.id || 'existing-0',
          previewUrl: fieldData.previewUrl || fieldData.url,
          fileName:
            fieldData.fileName ||
            this.extractFileNameFromUrl(fieldData.previewUrl || fieldData.url),
          fileSize: fieldData.fileSize || 0,
          isExisting: true,
          onFileChanged: false,
          objectName: fieldData.objectName,
        };
        this.existingFilesCache.set(field.controlName, [convertedFile]);
        return;
      }

      // Handle multi-file fields or file fields with array
      if (
        field.type === 'multi-file' ||
        (field.type === 'file' && Array.isArray(fieldData))
      ) {
        if (Array.isArray(fieldData)) {
          const convertedFiles = fieldData.map((file: any, index: number) => ({
            id: file.id || `existing-${index}`,
            previewUrl: file.previewUrl || file.url,
            fileName:
              file.fileName ||
              this.extractFileNameFromUrl(file.previewUrl || file.url),
            fileSize: file.fileSize || 0,
            isExisting: true,
            onFileChanged: false,
            objectName: file.objectName,
          }));
          this.existingFilesCache.set(field.controlName, convertedFiles);
        }
      }
    });
  }

  /**
   * Check if there are multiple detail images
   */
  public hasMultipleDetailImages(): boolean {
    const detailImagesField = this.formFieldsSignal().find(
      (f) => f.controlName === 'detailedImage' && f.type === 'galleria-display',
    );

    if (detailImagesField) {
      // For galleria-display type, get the field data directly from initialData
      const fieldData = this.initialData?.['detailedImage'];
      let existingFiles: any[] = [];

      if (Array.isArray(fieldData)) {
        existingFiles = fieldData;
      }

      return existingFiles.length > 1;
    }

    return false;
  }

  // Multi-file upload methods
  onMultiFileUpload(files: any[], fieldName: string): void {
    this.form.get(fieldName)?.setValue(files);
    this.modifiedControls.add(fieldName);

    // Emit file change event to parent component
    this.fileChanged.emit({ fieldName, fileData: files });

    // Emit form values to parent
    this.emitFormValues();
  }

  onMultiFileRemoved(removedFileIds: string[], fieldName: string): void {
    // Store removed file IDs for later processing
    const currentRemovedIds =
      this.form.get(`${fieldName}_removed`)?.value || [];
    const updatedRemovedIds = [...currentRemovedIds, ...removedFileIds];
    this.form.get(`${fieldName}_removed`)?.setValue(updatedRemovedIds);

    this.modifiedControls.add(fieldName);

    // Emit form values to parent
    this.emitFormValues();
  }

  onExistingImagesReordered(files: any[], fieldName: string): void {
    this.existingImagesReordered.emit({ fieldName, files });
  }

  /**
   * Get i18n placeholder for upload fields based on upload detail config
   */
  getUploadPlaceholder(field: any, uploadConfig: any = null): string {
    // Use field placeholder or default translation
    return (
      this.translateService.instant(field.placeholder, field.placeholderParams) ||
      this.translateService.instant(
        'common.form.placeholders.click_to_upload_image',
      )
    );
  }

  /**
   * Get i18n accepted types text based on upload detail config
   */
  getAcceptedTypesText(field: any, uploadConfig: any = null): string {
    // Use field acceptedTypes or default
    return field.acceptedTypes || 'JPG, JPEG, PNG, GIF, WEBP';
  }

  onFileInputChange(fieldName: string, event: any): void {
    const file = event.target.files[0];
    if (file) {
      const previewUrl = URL.createObjectURL(file);
      const fileData = {
        file,
        previewUrl,
        fileName: file.name,
        fileSize: file.size,
      };

      // Store file data in form
      this.form.get(fieldName)?.setValue(fileData);
      this.emitFormValues();
    }
  }

  getFilePreview(fieldName: string): string | null {
    const fileData = this.form.get(fieldName)?.value;
    return fileData?.previewUrl || null;
  }

  getImagePreview(fieldName: string): any {
    const fileData = this.form.get(fieldName)?.value;
    return fileData || null;
  }

  getGalleriaImages(fieldName: string): GalleriaImage[] {
    const formValue = this.form.get(fieldName)?.value;
    if (!formValue) return [];

    // Handle both array and single object formats
    const images = Array.isArray(formValue) ? formValue : [formValue];

    return images
      .filter((img) => img && (img.previewUrl || img.src))
      .map((img, index) => ({
        id: img.id || `img-${index}`,
        src: img.previewUrl || img.src,
        thumbnailSrc: img.thumbnailSrc || img.previewUrl || img.src,
        alt: img.fileName || img.alt || `Gallery item ${index + 1}`,
        title: img.fileName || img.title || '',
        description: img.description || '',
      }));
  }

  // Get detailed images from signal (computed, no method calls in template)
  getDetailedImages(): GalleriaImage[] {
    return this._detailedImagesSignal();
  }

  // Get main images from signal (computed, no method calls in template)
  getMainImages(): GalleriaImage[] {
    return this._mainImagesSignal();
  }

  // Update detailed images signal only when form value actually changes
  private updateDetailedImages(): void {
    const formValue = this.form.get('detailedImage')?.value;

    // Only update if value actually changed
    if (this.isValueEqual(this._lastDetailedImageValue, formValue)) {
      return;
    }

    this._lastDetailedImageValue = formValue;
    this.processDetailedImages(formValue);
  }

  // Process detailed images and update signal
  private processDetailedImages(formValue: any): void {
    if (!formValue) {
      this._detailedImagesSignal.set([]);
      return;
    }

    // Handle both array and single object formats
    let images = [];
    if (Array.isArray(formValue)) {
      images = formValue;
    } else {
      images = [formValue];
    }

    const result = images
      .filter((img) => {
        const hasPreview = img && (img.previewUrl || img.src);
        return hasPreview;
      })
      .map((img, index) => ({
        id: img.id || `img-${index}`,
        src: img.previewUrl || img.src,
        thumbnailSrc: img.thumbnailSrc || img.previewUrl || img.src,
        alt: img.fileName || img.alt || `Gallery item ${index + 1}`,
        title: img.fileName || img.title || '',
        description: img.description || '',
        fileSize: img.fileSize,
      }));

    this._detailedImagesSignal.set(result);

    // Trigger change detection for dynamic labels
    this.updateDynamicLabels();
  }

  // Public method to manually update detailed images (call this when material data is loaded)
  public updateDetailedImagesFromMaterial(materialImages: any): void {
    this._lastDetailedImageValue = materialImages;
    this.processDetailedImages(materialImages);
  }

  // Update main images signal only when form value actually changes
  private updateMainImages(): void {
    const formValue = this.form.get('mainImage')?.value;

    // Only update if value actually changed
    if (this.isValueEqual(this._lastMainImageValue, formValue)) {
      return;
    }

    this._lastMainImageValue = formValue;
    this.processMainImages(formValue);
  }

  // Process main images and update signal
  private processMainImages(formValue: any): void {
    if (!formValue) {
      this._mainImagesSignal.set([]);
      return;
    }

    let images = Array.isArray(formValue) ? formValue : [formValue];
    const galleriaImages = images
      .filter((img) => img && (img.previewUrl || img.src))
      .map((img, index) => ({
        id: img.id || `main-img-${index}`,
        src: img.previewUrl || img.src,
        thumbnailSrc: img.thumbnailSrc || img.previewUrl || img.src,
        alt: img.fileName || img.alt || `Main image ${index + 1}`,
        title: img.fileName || img.title || '',
        description: img.description || '',
        fileSize: img.fileSize,
      }));

    this._mainImagesSignal.set(galleriaImages);

    // Trigger change detection for dynamic labels
    this.updateDynamicLabels();
  }

  // Public method to manually update main images (call this when material data is loaded)
  public updateMainImagesFromMaterial(materialImages: any): void {
    this._lastMainImageValue = materialImages;
    this.processMainImages(materialImages);
  }

  // Legacy method for other fields (if needed)
  getConditionalImages(fieldName: string): GalleriaImage[] {
    if (fieldName === 'detailedImage') {
      const images = this.getDetailedImages();
      return images;
    }

    if (fieldName === 'mainImage') {
      const images = this.getMainImages();
      return images;
    }

    // For other fields, use the old method
    const formValue = this.form.get(fieldName)?.value;
    if (!formValue) return [];

    let images = Array.isArray(formValue) ? formValue : [formValue];
    return images
      .filter((img) => img && (img.previewUrl || img.src))
      .map((img, index) => ({
        id: img.id || `img-${index}`,
        src: img.previewUrl || img.src,
        thumbnailSrc: img.thumbnailSrc || img.previewUrl || img.src,
        alt: img.fileName || img.alt || `Gallery item ${index + 1}`,
        title: img.fileName || img.title || '',
        description: img.description || '',
        fileSize: img.fileSize,
        isVideo: !!img.isVideo,
        videoUrl: img.isVideo ? img.previewUrl || img.videoUrl : undefined,
        videoType: img.isVideo ? img.usageVideoType : undefined,
        videoExternalPlatform: img.isVideo
          ? img.usageVideoExternalPlatform
          : undefined,
      }));
  }

  // Converts a YouTube/Vimeo page URL (what <video> can't play) to its embeddable player URL.
  getVideoEmbedUrl(
    url: string,
    platform: 'YOUTUBE' | 'VIMEO' | undefined,
  ): SafeResourceUrl | null {
    if (!url) return null;
    let embedUrl: string | null = null;
    if (platform === 'YOUTUBE') {
      const match = url.match(
        /(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]+)/i,
      );
      embedUrl = match ? `https://www.youtube.com/embed/${match[1]}` : null;
    } else if (platform === 'VIMEO') {
      const match = url.match(/vimeo\.com\/(?:video\/)?(\d+)/i);
      embedUrl = match ? `https://player.vimeo.com/video/${match[1]}` : null;
    }
    return embedUrl
      ? this.sanitizer.bypassSecurityTrustResourceUrl(embedUrl)
      : null;
  }

  getGalleriaConfig(field: FormField): GalleriaConfig {
    return {
      showThumbnails: true,
      showIndicators: true,
      showItemNavigators: true,
      showThumbnailNavigators: true,
      showDownloadButton: false,
      autoPlay: false,
      circular: true,
      numVisible: 5,
      transitionInterval: 4000,
      responsiveOptions: [
        {
          breakpoint: '1024px',
          numVisible: 5,
        },
        {
          breakpoint: '768px',
          numVisible: 3,
        },
        {
          breakpoint: '560px',
          numVisible: 1,
        },
      ],
    };
  }

  // Helper method for deep value comparison
  private isValueEqual(a: any, b: any): boolean {
    if (a === b) return true;
    if (a == null || b == null) return false;
    if (typeof a !== typeof b) return false;

    if (Array.isArray(a) && Array.isArray(b)) {
      if (a.length !== b.length) return false;
      return a.every((val, index) => this.isValueEqual(val, b[index]));
    }

    if (typeof a === 'object') {
      const keysA = Object.keys(a);
      const keysB = Object.keys(b);
      if (keysA.length !== keysB.length) return false;
      return keysA.every((key) => this.isValueEqual(a[key], b[key]));
    }

    return false;
  }

  // Safe form value comparison that handles circular references
  private isFormValueEqual(prev: any, curr: any): boolean {
    if (prev === curr) return true;
    if (prev == null || curr == null) return false;

    // Handle Date objects - compare by time value
    if (prev instanceof Date && curr instanceof Date) {
      return prev.getTime() === curr.getTime();
    }
    if (prev instanceof Date || curr instanceof Date) return false;

    if (typeof prev !== typeof curr) return false;

    // Handle tree structures (like treeselect values) by comparing only essential properties
    if (this.isTreeStructure(prev) && this.isTreeStructure(curr)) {
      return this.compareTreeValues(prev, curr);
    }

    if (Array.isArray(prev) && Array.isArray(curr)) {
      if (prev.length !== curr.length) return false;
      return prev.every((val, index) =>
        this.isFormValueEqual(val, curr[index]),
      );
    }

    if (typeof prev === 'object') {
      const keysA = Object.keys(prev);
      const keysB = Object.keys(curr);
      if (keysA.length !== keysB.length) return false;
      return keysA.every((key) => this.isFormValueEqual(prev[key], curr[key]));
    }

    return prev === curr;
  }

  // Check if value is a tree structure (has children property)
  private isTreeStructure(value: any): boolean {
    return (
      value &&
      typeof value === 'object' &&
      (value.children !== undefined || value.value !== undefined)
    );
  }

  // Compare tree values by comparing essential properties only
  private compareTreeValues(a: any, b: any): boolean {
    // Compare by value property if it exists (for treeselect)
    if (a.value !== undefined && b.value !== undefined) {
      return a.value === b.value;
    }

    // Compare by key if it exists
    if (a.key !== undefined && b.key !== undefined) {
      return a.key === b.key;
    }

    // Compare by label if it exists
    if (a.label !== undefined && b.label !== undefined) {
      return a.label === b.label;
    }

    // Fallback to basic comparison
    return a === b;
  }

  // Safe stringify that handles circular references
  private safeStringify(obj: any): string {
    const seen = new WeakSet();
    return JSON.stringify(obj, (key, val) => {
      if (val != null && typeof val === 'object') {
        if (seen.has(val)) {
          return '[Circular]';
        }
        seen.add(val);
      }
      return val;
    });
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return (
      Number.parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
    );
  }

  getFileName(fieldName: string): string {
    const fileData = this.form.get(fieldName)?.value;
    return fileData?.fileName || '';
  }

  removeFile(fieldName: string): void {
    const fileData = this.form.get(fieldName)?.value;
    if (fileData?.previewUrl) {
      URL.revokeObjectURL(fileData.previewUrl);
    }
    this.form.get(fieldName)?.setValue(null);
    this.emitFormValues();
  }

  private hasMaxLengthError(): boolean {
    return Object.values(this.form.controls).some(
      (c) => !c.disabled && !!c.errors?.['maxlength'],
    );
  }

  private markFormGroupTouched(): void {
    // Use markAllAsTouched() to mark all controls as touched
    this.form.markAllAsTouched();

    // Process all controls to ensure validation messages show
    Object.keys(this.form.controls).forEach((key) => {
      const control = this.form.get(key);
      if (control) {
        // For invalid controls, mark as dirty to ensure validation messages show
        if (control.invalid) {
          control.markAsDirty();
          // Trigger validation to ensure errors are set
          control.updateValueAndValidity({ emitEvent: false });
        }

        // For dropdown fields, always set selection state to ensure validation messages show
        // This is needed because shouldShowError() checks dropdownSelectionState
        const field = this.formFieldsSignal().find(
          (f) => f.controlName === key,
        );
        if (
          field?.type === 'dropdown' ||
          field?.type === 'searchableDropdown'
        ) {
          this.dropdownSelectionState[key] = true;
        }
      }
    });

    // Trigger change detection to display validation messages
    // This is required with OnPush change detection strategy
    this.cdr.markForCheck();
  }

  public getInvalidFieldNames(): string[] {
    const invalidControls = new Set(
      Object.keys(this.form.controls).filter(
        (key) => this.form.controls[key].invalid,
      ),
    );

    return this.formFieldsSignal()
      .filter((field) => invalidControls.has(field.controlName))
      .map((field) => field.label);
  }

  getErrorMessage(field: FormField): string {
    const control = this.form.get(field.controlName);
    if (!control?.errors) return '';
    const errorType = Object.keys(control.errors)[0];
    if (field.errorMessages?.[errorType]) return field.errorMessages[errorType];
    switch (errorType) {
      case 'required':
        return `common.validation.required`;
      case 'maxlength':
        return `common.validation.maxlength`;
      case 'min':
        return `common.validation.min`;
      case 'max':
        return `common.validation.max`;
      default:
        return 'common.validation.invalid';
    }
  }

  getErrorParams(field: FormField): Record<string, unknown> {
    const control = this.form.get(field.controlName);
    const err = control?.errors?.['maxlength'];
    if (!err) return {};
    return {
      fieldName: this.translateService.instant(field.label || ''),
      requiredLength: err.requiredLength,
    };
  }

  getHtmlDescriptionConfig(field: FormField): any {
    return {
      placeholder: this.translateService.instant(field.placeholder || ''),
      height: field.customStyles?.height || '200px',
      minHeight: field.customStyles?.minHeight || '120px',
      maxHeight: field.customStyles?.maxHeight || '400px',
      maxLength: field.maxLength,
      readonly: this.isFieldDisabled(field) || this.viewMode,
      disabled: this.isFieldDisabled(field) || this.viewMode,
      required: field.required || false,
      showToolbar: !this.viewMode,
    };
  }

  isFieldDisabled(field: FormField): boolean {
    if (field.disabled) {
      return true;
    }
    if (!field.dependsOn) {
      return false;
    }
    const parentControl = this.form.get(field.dependsOn);

    if (parentControl) {
      const parentValue = parentControl.value;

      // Special case for maxDiscountAmount field when couponType is FLAT_DISCOUNT
      if (
        field.controlName === 'maxDiscountAmount' &&
        field.dependsOn === 'couponType'
      ) {
        return parentValue === 'FLAT_DISCOUNT';
      }

      // For checkbox dependencies, check the actual boolean value
      // Handle both boolean and number values (0/1 for divide flag)
      const isCheckboxDependency = parentValue === true || parentValue === 1;

      // For string dropdown dependencies, check if parent has a value
      const hasValue =
        parentValue !== null && parentValue !== undefined && parentValue !== '';

      // If it's a checkbox dependency, disable if parent is not true
      if (isCheckboxDependency) {
        return false; // Enable if checkbox is checked
      }

      // For string dropdown dependencies, disable if parent has no value
      return !hasValue;
    }

    return true;
  }

  /**
   * When focusing a field that shows "0 + unit" (currency or number with suffix),
   * clear the default 0 so the user can type directly without deleting it first.
   */
  onUnitFieldFocus(field: FormField): void {
    if (this.viewMode || this.isFieldDisabled(field)) return;
    const control = this.form?.get(field.controlName);
    if (control?.value === 0) {
      control.setValue(null, { emitEvent: false });
    }
  }

  /**
   * When blurring a unit field, if the value is empty and the field allows 0,
   * restore 0 so it displays "0 + unit" again.
   */
  onUnitFieldBlur(field: FormField): void {
    if (this.viewMode || this.isFieldDisabled(field)) return;
    const control = this.form?.get(field.controlName);
    if (!control) return;
    const val = control.value;
    const isEmpty = val === null || val === undefined || val === '';
    const allowsZero =
      field.min === 0 || field.min === undefined || field.defaultValue === 0;
    if (isEmpty && allowsZero) {
      control.setValue(0, { emitEvent: false });
    }
  }

  formatSummaryValue(item: SummaryItem): string {
    if (typeof item.value === 'string') return item.value;

    switch (item.formatter) {
      case 'currency': {
        const value = item.value;
        const currency = item.currency || 'USD';
        switch (currency) {
          case 'CNY':
            return `${new Intl.NumberFormat('zh-CN').format(value)}`;
          case 'VND':
            return this.formatCurrency(value);
          case 'USD':
            return `$${new Intl.NumberFormat('en-US').format(value)}`;
          default:
            return new Intl.NumberFormat('en-US', {
              style: 'currency',
              currency: currency,
            }).format(value);
        }
      }
      case 'number':
        return new Intl.NumberFormat('en-US').format(item.value);
      default:
        return item.value.toString();
    }
  }

  getFieldLabel(field: FormField): string {
    if (field.translateLabel === false) {
      // eslint-disable-next-line deprecation/deprecation
      const lang = this.translateService.currentLang || 'en';
      if (lang.startsWith('vi') && field.labelVi) {
        return field.labelVi;
      }
      return field.label;
    }
    // Check if field has dynamic label configuration
    if (field.dynamicLabel) {
      const dynamicLabel = this.getDynamicLabel(field);
      return dynamicLabel;
    }

    return field.label;
  }

  /** Language-aware helper text shown under a field (empty string = no hint). */
  getFieldHint(field: FormField): string {
    if (!field.hint) {
      return '';
    }
    if (field.translateHint) {
      return this.translateService.instant(field.hint);
    }
    // eslint-disable-next-line deprecation/deprecation
    const lang = this.translateService.currentLang || 'en';
    if (lang.startsWith('vi') && field.hintVi) {
      return field.hintVi;
    }
    return field.hint;
  }

  getFieldPlaceholder(field: FormField): string {
    if (!field.placeholder) {
      return '';
    }
    if (field.translateLabel === false) {
      // eslint-disable-next-line deprecation/deprecation
      // eslint-disable-next-line deprecation/deprecation
      const lang = this.translateService.currentLang || 'en';
      if (lang.startsWith('vi') && field.placeholderVi) {
        return field.placeholderVi;
      }
      return field.placeholder;
    }
    return this.translateService.instant(field.placeholder);
  }

  getVideoPlatformIconClass(field: FormField): string | null {
    const value = this.form.get(field.controlName)?.value;
    if (typeof value !== 'string' || !value.trim()) return null;
    if (/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)/i.test(value)) {
      return 'pi-youtube';
    }
    if (/vimeo\.com\//i.test(value)) {
      return 'pi-vimeo';
    }
    return null;
  }

  private getDynamicLabel(field: FormField): string {
    if (!this.form) return field.label;

    // Get the base label from field configuration
    const baseLabel = field.label;

    // Check for dynamic label rules
    if (field.dynamicLabel?.type === 'coupon') {
      const couponType = this.form.get('type')?.value;
      const unit = this.getCouponUnit(couponType);

      // Replace placeholders in the label
      return baseLabel.replace('{unit}', unit);
    }

    // Check for image count dynamic label
    if (field.dynamicLabel?.type === 'image-count') {
      const imageCount = this.getImageCount(field.controlName);
      if (imageCount <= 1) {
        return field.dynamicLabel.singularLabel || baseLabel;
      } else {
        return field.dynamicLabel.pluralLabel || baseLabel;
      }
    }

    // Check for custom dynamic label rules
    if (field.dynamicLabel?.type === 'custom' && field.dynamicLabel.rules) {
      // Check both field.dynamicLabel.dependsOn and field.dependsOn
      const dependsOnField = field.dynamicLabel.dependsOn || field.dependsOn;
      const dependsOnValue = dependsOnField
        ? this.form.get(dependsOnField)?.value
        : null;

      for (const rule of field.dynamicLabel.rules) {
        if (rule.condition(dependsOnValue)) {
          return rule.label;
        }
      }
    }

    return baseLabel;
  }

  private getImageCount(controlName: string): number {
    if (controlName === 'mainImage') {
      const count = this.getMainImages().length;
      return count;
    } else if (controlName === 'detailedImage') {
      const count = this.getDetailedImages().length;
      return count;
    }

    // For other fields, check form value
    const formValue = this.form.get(controlName)?.value;
    if (!formValue) return 0;

    if (Array.isArray(formValue)) {
      return formValue.length;
    }

    return 1;
  }

  private getCouponUnit(couponType: string): string {
    switch (couponType) {
      case 'PERCENTAGE_DISCOUNT':
        return '(%)';
      case 'FIXED_AMOUNT_DISCOUNT':
        return '(VND)';
      default:
        return '';
    }
  }

  shouldShowError(fieldName: string): boolean {
    const control = this.form.get(fieldName);
    if (!control) return false;

    // For dropdown fields, be very conservative about showing errors
    const field = this.formFieldsSignal().find(
      (f) => f.controlName === fieldName,
    );
    if (field?.type === 'dropdown' || field?.type === 'searchableDropdown') {
      // Show error if dropdown is invalid and has been touched
      // dropdownSelectionState is set when form is marked as touched
      return (
        control.invalid &&
        (control.touched || this.dropdownSelectionState[fieldName]) &&
        control.errors !== null
      );
    }

    // For number fields, ensure they show errors when validation fails
    if (field?.type === 'number') {
      return control.invalid && (control.dirty || control.touched);
    }

    // For phone validation, show error while typing if data is invalid
    // Use a more stable approach to prevent flickering
    if (
      (field?.controlName === 'contactPhone' ||
        field?.controlName === 'phone' ||
        field?.controlName === 'returnsTel') &&
      (control.errors?.['invalidPhone'] ||
        control.errors?.['invalidVietnamesePhone'])
    ) {
      // Show error if field has been touched or has a value (to show while typing)
      return (
        control.invalid &&
        (control.touched || (control.value && control.value.length > 0))
      );
    }

    // For other fields, use the standard approach
    return control.invalid && (control.dirty || control.touched);
  }

  shouldShowErrorStyling(fieldName: string): boolean {
    return this.shouldShowError(fieldName);
  }

  getCustomStyles(field: FormField): any {
    if (!field.customStyles) return {};

    return field.customStyles;
  }

  /**
   * Get list of controls that have been modified by the user
   */
  private getModifiedControls(): string[] {
    return Array.from(this.modifiedControls);
  }

  /**
   * Clear removed images arrays (useful when starting a new form)
   */
  clearRemovedImages(): void {
    this.removedImages = [];
    this.removedMainImages = [];
  }

  /**
   * Force-apply authoritative initial values (e.g. draft proposedCategory),
   * clearing modified-control protection so async reloads are not skipped.
   */
  applyAuthoritativeInitialData(data: Record<string, unknown>): void {
    this.modifiedControls.clear();
    this._previousInitialData = null;
    this._initialData = data;
    if (this.form && data) {
      this.form.patchValue(data, { emitEvent: false });
      setTimeout(() => this.initializeConditionalFields(), 0);
    }
    this.cdr.markForCheck();
  }

  /**
   * Prepare form data for submission, filtering out unchanged files
   */
  getFormDataForSubmission(): any {
    const formValue = {
      ...this.form.getRawValue(),
      selectedRadioValues: this.selectedRadioValues,
      additionalValues: this.additionalValues,
    };
    const processedData = { ...formValue };

    // Always rebuild detailImages from upload component submission data.
    // This prevents re-sending existing unchanged images from raw form state.
    delete processedData.detailImages;

    this.processMainImageForSubmission(processedData);

    const multiFileData = this.collectMultiFileImagesData();
    const { files: detailImages = [], removed: removeDetailImages = [] } =
      multiFileData['detailImages'] ?? { files: [], removed: [] };

    const detailImageControls = Object.keys(processedData).filter((key) =>
      key.startsWith('detailImage_'),
    );

    detailImageControls.forEach((controlName) => {
      const imageData = processedData[controlName];
      if (imageData?.onFileChanged) {
        detailImages.push(imageData.file);
      }
    });

    detailImageControls.forEach((controlName) => {
      delete processedData[controlName];
    });

    if (detailImages.length > 0) {
      processedData.detailImages = detailImages;
    }

    if (this.removedMainImages?.length > 0) {
      processedData.removedMainImages = this.removedMainImages;
    }

    if (removeDetailImages.length > 0) {
      processedData.removedDetailImages = removeDetailImages;
    } else if (this.removedImages?.length > 0) {
      processedData.removedDetailImages = this.removedImages;
    }

    // Any other multi-file field beyond the legacy "detailImages" one above:
    // rebuild from submission data the same way, generically.
    Object.keys(multiFileData)
      .filter((fieldName) => fieldName !== 'detailImages')
      .forEach((fieldName) => {
        const { files, removed } = multiFileData[fieldName];
        if (files.length > 0) {
          processedData[fieldName] = files;
        } else {
          delete processedData[fieldName];
        }
        if (removed.length > 0) {
          const removedKey = `removed${fieldName.charAt(0).toUpperCase()}${fieldName.slice(1)}`;
          processedData[removedKey] = removed;
        }
      });

    return processedData;
  }

  private extractFileNameFromUrl(url: string): string {
    if (!url) return 'unknown-file';

    try {
      const urlObj = new URL(url);
      const pathname = urlObj.pathname;
      const fileName = pathname.split('/').pop() || 'unknown-file';

      // Decode so it matches the API's own plain (non-percent-encoded) fileName field.
      return decodeURIComponent(fileName.split('?')[0].split('#')[0]);
    } catch {
      // If URL parsing fails, try to extract filename from the string
      const fileName = url.split('/').at(-1) || 'unknown-file';
      return decodeURIComponent(fileName.split('?')[0].split('#')[0]);
    }
  }

  private createHtmlMaxLengthValidator(maxLength: number) {
    return (control: any) => {
      if (!control.value) return null;

      // Strip HTML tags and count characters
      const textContent = control.value.replaceAll(/<[^>]*>/g, '');
      const length = textContent.length;

      if (length > maxLength) {
        return {
          maxlength: { requiredLength: maxLength, actualLength: length },
        };
      }

      return null;
    };
  }

  // Handle image loading errors
  onImageError(event: Event): void {
    const img = event.target as HTMLImageElement;
    img.style.display = 'none';
  }

  isArray(value: any): boolean {
    return Array.isArray(value);
  }

  getShowClearForField(field: FormField): boolean {
    if (!field.showClear) {
      return false;
    }

    const control = this.form.get(field.controlName);
    const value = control?.value;

    // Show clear icon only if there's a value to clear
    return value !== null && value !== undefined && value !== '';
  }

  // Handle table cell value changes
  onTableCellValueChange(
    sectionTitle: string,
    event: { rowData: any; prop: string; value: any },
  ): void {
    // Find the table section and update the data
    const updatedSections = this.tableSectionsSignal().map((tableSection) => {
      if (tableSection.title === sectionTitle) {
        const updatedData = tableSection.data.map((item) =>
          item.id === event.rowData.id
            ? { ...item, [event.prop]: event.value }
            : item,
        );
        return {
          ...tableSection,
          data: updatedData,
        };
      }
      return tableSection;
    });

    // Update the table sections
    this.tableSectionsSignal.set(updatedSections);

    // Emit the change to parent component so it can update its signals
    this.tableAction.emit({
      section: sectionTitle,
      action: 'cellValueChange',
      data: event,
    });
  }

  // Live input change passthrough for realtime validation
  onTableCellInputChange(
    sectionTitle: string,
    event: { rowData: any; prop: string; value: any },
  ): void {
    this.tableAction.emit({
      section: sectionTitle,
      action: 'cellInputChange',
      data: event,
    });
  }

  // Handle tax validation button click
  onTaxValidationClick(): void {
    this.taxValidationClick.emit();
  }

  // Get coupon chip value for display
  getCouponChipValue(controlName: string): string | null {
    const field = this.formFieldsSignal().find(
      (f) => f.controlName === controlName,
    );
    return field?.couponChipValue || null;
  }

  // Handle coupon chip removal
  onCouponChipRemove(controlName: string): void {
    const field = this.formFieldsSignal().find(
      (f) => f.controlName === controlName,
    );
    if (field?.onCouponChipRemove) {
      field.onCouponChipRemove();
    }
  }

  // Get multiple coupon chips for display
  getCouponChips(controlName: string): string[] {
    const field = this.formFieldsSignal().find(
      (f) => f.controlName === controlName,
    );
    return field?.couponChips || [];
  }

  // Handle removal of a specific coupon chip by code
  onCouponChipRemoveByCode(controlName: string, code: string): void {
    const field = this.formFieldsSignal().find(
      (f) => f.controlName === controlName,
    );
    if (field?.onCouponChipRemoveByCode) {
      field.onCouponChipRemoveByCode(code);
    }
  }

  // Find the coupon field (if any) to support full-width chip rendering
  getCouponField(): FormField | undefined {
    return this.formFieldsSignal().find(
      (f) => f.type === 'couponAutocomplete' && !f.hidden,
    );
  }

  // Find the coupon field in a specific section (used to render chips under that section)
  getCouponFieldInSection(section: string): FormField | undefined {
    return this.formFieldsSignal().find(
      (f) =>
        f.type === 'couponAutocomplete' && f.section === section && !f.hidden,
    );
  }

  // Helper methods for status display in advanced table
  getStatusDisplayText(status: string): string {
    if (!status) return '';
    if (status === 'SUCCESS') {
      return this.translateService.instant(
        'onlineStore.form.product_list.status.success',
      );
    }
    if (status === 'DRAFT') {
      return this.translateService.instant(
        'onlineStore.form.product_list.status.draft',
      );
    }
    // Fallback for other statuses
    if (status === 'ACTIVE') {
      return this.translateService.instant(
        'onlineStore.form.product_list.status.active',
      );
    }
    return this.translateService.instant(
      'onlineStore.form.product_list.status.inactive',
    );
  }

  getStatusSeverity(status: string): 'warning' | 'success' | 'danger' | 'info' {
    if (!status) return 'warning';
    switch (status) {
      case 'SUCCESS':
        return 'success';
      case 'DRAFT':
        return 'danger';
      case 'ACTIVE':
        return 'success';
      case 'INACTIVE':
        return 'danger';
      default:
        return 'warning';
    }
  }

  getStatusStyle(status: string): any {
    if (!status) return {};
    switch (status) {
      case 'SUCCESS':
      case 'ACTIVE':
        return {
          '--status-color': '#22c55e',
        };
      case 'DRAFT':
      case 'INACTIVE':
        return {
          '--status-color': '#ef4444',
        };
      default:
        return {};
    }
  }

  getIssuedCouponStatusLabel(status: string): string {
    if (!status) return '';
    const upper = String(status).toUpperCase();
    if (upper === 'ACTIVE') {
      return this.translateService.instant('issued_coupon.status.active');
    }
    if (upper === 'VOID') {
      return this.translateService.instant('issued_coupon.status.void');
    }
    if (upper === 'EXHAUSTED') {
      return this.translateService.instant('issued_coupon.status.exhausted');
    }
    return upper;
  }

  getIssuedCouponStatusSeverity(
    status: string,
  ): 'success' | 'danger' | 'warn' | 'secondary' {
    const upper = String(status || '').toUpperCase();
    if (upper === 'ACTIVE') return 'success';
    if (upper === 'VOID') return 'danger';
    if (upper === 'EXHAUSTED') return 'warn';
    return 'secondary';
  }

  getIssuedCouponStatusStyle(status: string): Record<string, string> {
    const upper = String(status || '').toUpperCase();
    if (upper === 'ACTIVE') {
      return {
        'background-color': '#ecfdf5',
        color: '#047857',
        border: 'none',
      };
    }
    if (upper === 'VOID') {
      return {
        'background-color': '#fef2f2',
        color: '#dc2626',
        border: 'none',
      };
    }
    if (upper === 'EXHAUSTED') {
      return {
        'background-color': '#fef3c7',
        color: '#a16207',
        border: 'none',
      };
    }
    return {
      'background-color': '#f3f4f6',
      color: '#374151',
      border: 'none',
    };
  }

  getDropdownDisplayValue(field: FormField): string {
    const control = this.form.get(field.controlName);
    const value = control?.value;
    if (value === null || value === undefined || value === '') {
      return '';
    }
    const options = this.getFieldOptions(field);
    const option = options.find((opt: any) => opt.value === value);
    return option?.label || value;
  }

  getMultipleDropdownDisplayValue(field: FormField): string {
    const control = this.form.get(field.controlName);
    const values = control?.value;
    if (!values || !Array.isArray(values) || values.length === 0) {
      return '';
    }
    const options = this.getFieldOptions(field);
    const labels = values
      .map((val: any) => {
        const option = options.find((opt: any) => opt.value === val);
        return option?.label || val;
      })
      .filter(Boolean) as string[];
    return labels.join(', ');
  }

  // ── initialData helper methods ──────────────────────────────────────────────

  private hasKeyFieldData(obj: any): boolean {
    if (!obj || typeof obj !== 'object') return false;
    const keyFields = [
      'categoryName',
      'categoryCode',
      'name',
      'offerName',
      'code',
      'id',
    ];
    return keyFields.some((field) => {
      const value = obj[field];
      return value !== null && value !== undefined && value !== '';
    });
  }

  private preserveAndRestoreImageCache(data: any): void {
    const preservedMainImage = this.existingFilesCache.get('mainImage');
    const preservedDetailImages = this.existingFilesCache.get('detailImages');
    this.existingFilesCache.clear();

    if (preservedMainImage && preservedMainImage.length > 0) {
      if (
        !data?.mainImage ||
        (Array.isArray(data.mainImage) && data.mainImage.length === 0)
      ) {
        this.existingFilesCache.set('mainImage', preservedMainImage);
      }
    }

    if (preservedDetailImages && preservedDetailImages.length > 0) {
      if (
        !data?.detailImages ||
        (Array.isArray(data.detailImages) && data.detailImages.length === 0)
      ) {
        this.existingFilesCache.set('detailImages', preservedDetailImages);
      }
    }
  }

  private patchExistingForm(data: any): void {
    const dataToPatch = this.buildDataToPatch(data);
    this.form.patchValue(dataToPatch);
    // Radio fields don't go through onRadioChange on a programmatic patch, so they never earn
    // modifiedControls protection on their own — without this a later recompute can silently
    // revert an already-loaded value, same class of bug the file/image handling below guards.
    this.formFieldsSignal()
      .filter((field) => field.type === 'radio' && dataToPatch[field.controlName] !== undefined)
      .forEach((field) => this.modifiedControls.add(field.controlName));
    setTimeout(() => this.initializeConditionalFields(), 0);
    if (this.hasNewImageData(data)) {
      this.setImageControlsAfterDelay(data, 50);
    }
  }

  private buildDataToPatch(data: any): Record<string, any> {
    const dataToPatch: Record<string, any> = { ...data };
    delete dataToPatch['couponDiscount'];

    const controlsToSkip = this.computeIsFirstLoad(data)
      ? []
      : this.getModifiedControls();
    controlsToSkip.forEach((name) => delete dataToPatch[name]);

    const mainImageControl = this.form.get('mainImage');
    const detailValue = this.form.get('detailImages')?.value;
    if (mainImageControl?.value)
      dataToPatch['mainImage'] = mainImageControl.value;
    if (Array.isArray(detailValue) && detailValue.length > 0)
      dataToPatch['detailImages'] = detailValue;

    return dataToPatch;
  }

  private computeIsFirstLoad(data: any): boolean {
    // Only key business fields count as "loaded data". Defaults like status:true
    // must not mark the form as already hydrated, or a later async patch
    // (e.g. category draft proposedCategory) is treated as a re-patch and can
    // be partially skipped via modifiedControls — RBA-3426.
    const prevHad = this.hasKeyFieldData(this._previousInitialData);
    const incoming = this.hasKeyFieldData(data);
    return (this.editMode || this.viewMode) && !prevHad && incoming;
  }

  private hasNewImageData(data: any): boolean {
    return (
      data?.mainImage != null ||
      (data?.detailImages &&
        Array.isArray(data.detailImages) &&
        data.detailImages.length > 0)
    );
  }

  private setImageControlsAfterDelay(data: any, delay: number): void {
    setTimeout(() => {
      if (data.mainImage != null) {
        const mainImageControl = this.form.get('mainImage');
        if (mainImageControl) {
          mainImageControl.setValue(data.mainImage, { emitEvent: true });
          this.updateMainImagesFromMaterial(data.mainImage);
        }
      }
      if (
        data.detailImages &&
        Array.isArray(data.detailImages) &&
        data.detailImages.length > 0
      ) {
        const detailImagesControl = this.form.get('detailImages');
        if (detailImagesControl) {
          detailImagesControl.setValue(data.detailImages, { emitEvent: true });
          this.updateDetailedImagesFromMaterial(data.detailImages);
        }
      }
      this.cdr.markForCheck();
    }, delay);
  }

  private initNewFormFromData(data: any): void {
    this.initForm();
    if (!this.form) return;
    this.form.patchValue(data, { emitEvent: false });
    this.cdr.markForCheck();
    if (this.hasNewImageData(data)) {
      this.setImageControlsAfterDelay(data, 100);
    }
  }

  // ── formFields helper methods ───────────────────────────────────────────────

  private hasFieldShapeChanged(prev: FormField[], next: FormField[]): boolean {
    const prevNames = new Set(prev.map((f) => f.controlName));
    const newNames = new Set(next.map((f) => f.controlName));
    if (prevNames.size !== newNames.size) return true;
    for (const name of newNames) {
      if (!prevNames.has(name)) return true;
    }
    return false;
  }

  private hasFieldOptionsChanged(
    prev: FormField[],
    next: FormField[],
  ): boolean {
    for (const newField of next) {
      if (
        newField.type !== 'dropdown' &&
        newField.type !== 'searchableDropdown'
      )
        continue;
      const prevField = prev.find(
        (f) => f.controlName === newField.controlName,
      );
      if (!prevField) continue;
      const newOptions = newField.options || [];
      const prevOptions = prevField.options || [];
      if (
        newOptions !== prevOptions ||
        newOptions.length !== prevOptions.length ||
        JSON.stringify(newOptions) !== JSON.stringify(prevOptions)
      ) {
        return true;
      }
    }
    return false;
  }

  private rebuildFormPreservingValues(): void {
    const oldValue = this.form.getRawValue?.() ?? this.form.value;
    this.initForm();
    if (oldValue) this.form.patchValue(oldValue, { emitEvent: false });
    if (this._initialData)
      this.form.patchValue(this._initialData, { emitEvent: false });
  }

  // ── initForm helper methods ─────────────────────────────────────────────────

  private buildFieldValidators(field: FormField): any[] {
    const validators: any[] = [];
    if (field.required) validators.push(Validators.required);
    if (field.maxLength) {
      if (field.type === 'html-description') {
        validators.push(this.createHtmlMaxLengthValidator(field.maxLength));
      } else {
        validators.push(Validators.maxLength(field.maxLength));
      }
    }
    if (field.min !== undefined) validators.push(Validators.min(field.min));
    if (field.max !== undefined) validators.push(Validators.max(field.max));
    if (field.validators) validators.push(...field.validators);
    return validators;
  }

  private registerFieldControl(group: any, field: FormField): void {
    const validators = this.buildFieldValidators(field);

    if (field.type === 'file') {
      if (
        field.controlName === 'detailImage' &&
        field.multiple &&
        (field as any).maxFiles
      ) {
        const maxFiles = (field as any).maxFiles;
        const existingImageCount = Array.isArray(this.initialData?.detailImages)
          ? this.initialData.detailImages.length
          : 0;
        for (let i = 0; i < Math.max(existingImageCount, maxFiles); i++) {
          group[this.getFileControlName(field.controlName, i)] = [
            null,
            validators,
          ];
        }
        return;
      }
      group[field.controlName] = [null, validators];
      return;
    }

    if (field.type === 'multi-file') {
      group[`${field.controlName}_removed`] = [[], []];
      const initialValue =
        this.initialData?.[field.controlName] === undefined
          ? []
          : this.initialData[field.controlName];
      group[field.controlName] = [initialValue, validators];
      return;
    }

    group[field.controlName] = [this.getFieldInitialValue(field), validators];
  }

  private getFieldInitialValue(field: FormField): any {
    if (field.type === 'couponAutocomplete') return field.defaultValue ?? [];
    if (field.type === 'checkbox') {
      if (this.initialData?.[field.controlName] !== undefined) {
        return this.initialData[field.controlName];
      }
      return field.defaultValue ?? false;
    }
    if (field.type === 'number') return field.defaultValue ?? null;
    if (
      (field.type === 'dropdown' || field.type === 'searchableDropdown') &&
      !field.required
    ) {
      return field.defaultValue ?? null;
    }
    if (this.initialData?.[field.controlName] === undefined) {
      return field.defaultValue ?? '';
    }
    return this.initialData[field.controlName];
  }

  // ── onTextInputChange helper methods ────────────────────────────────────────

  private handlePhoneFieldInput(
    _fieldName: string,
    event: any,
    _field: FormField,
    control: any,
  ): void {
    const value = event.target.value;
    const cleanedValue = value?.toString().replaceAll(/[\s\-().]/g, '') || '';
    control.setValue(value, { emitEvent: true });
    if (this.shouldValidatePhone(value, cleanedValue)) {
      control.markAsTouched();
      control.updateValueAndValidity({ emitEvent: true });
    } else {
      control.markAsUntouched();
      control.setErrors(null);
    }
    this.emitFormValues();
  }

  private handleNonPhoneFieldInput(
    fieldName: string,
    event: any,
    field: FormField | undefined,
    control: any,
  ): void {
    let value = event.target.value;
    let needsNormalization = false;
    const isComposing = this.compositionState.get(fieldName) || false;

    if (field?.validators && !isComposing) {
      const hasVietnameseValidator = field.validators.some((validator) => {
        if (typeof validator === 'function') {
          const validatorStr = validator.toString();
          return (
            validatorStr.includes('normalizeToNFC') ||
            validatorStr.includes('vietnameseTextPatternValidator')
          );
        }
        return false;
      });

      if (hasVietnameseValidator) {
        const normalized = this.textNormalizer.normalizeToNFC(value, false);
        if (normalized !== value) {
          value = normalized;
          needsNormalization = true;
        }
      }
    }

    const inputElement = event.target as HTMLInputElement;
    const cursorPosition = inputElement.selectionStart || 0;
    control.setValue(value, { emitEvent: true });

    if (needsNormalization && inputElement && !isComposing) {
      requestAnimationFrame(() => {
        const newCursorPosition = Math.min(cursorPosition, value.length);
        inputElement.setSelectionRange(newCursorPosition, newCursorPosition);
      });
    }
    this.emitFormValues();
  }

  // ── handleConditionalFields helper ─────────────────────────────────────────

  private applyConditionalFieldRule(
    field: FormField,
    dependentControl: any,
    value: any,
  ): boolean {
    if (
      field.controlName === 'maxDiscountAmount' &&
      field.dependsOn === 'couponType'
    ) {
      this.applyMaxDiscountAmountRule(dependentControl, value);
      return false;
    }

    const isActiveValue =
      value === true ||
      value === 1 ||
      (value !== null && value !== undefined && value !== '');

    if (isActiveValue) {
      dependentControl.enable({ emitEvent: false });
      return this.tryAutoSelect(field, dependentControl, value);
    } else if (field.passwordReadonlyOnly) {
      dependentControl.enable({ emitEvent: false });
    } else {
      dependentControl.disable({ emitEvent: false });
      dependentControl.setValue(null);
    }
    return false;
  }

  // ── initializeConditionalFields helper ─────────────────────────────────────

  private resolveInitialConditionalState(
    field: FormField,
    parentControl: any,
    dependentControl: any,
  ): boolean {
    const parentValue = parentControl.value;

    if (
      field.controlName === 'maxDiscountAmount' &&
      field.dependsOn === 'couponType'
    ) {
      this.applyMaxDiscountAmountRule(dependentControl, parentValue);
      return false;
    }

    const isCheckboxDependency = parentValue === true || parentValue === 1;
    const hasValue =
      parentValue !== null && parentValue !== undefined && parentValue !== '';

    if (!isCheckboxDependency && !hasValue) {
      if (field.passwordReadonlyOnly) {
        dependentControl.enable({ emitEvent: false });
      } else {
        dependentControl.disable({ emitEvent: false });
        dependentControl.setValue(null);
      }
      return false;
    }

    dependentControl.enable({ emitEvent: false });
    return this.tryAutoSelect(field, dependentControl, parentValue);
  }

  private applyMaxDiscountAmountRule(dependentControl: any, value: any): void {
    if (value === 'FLAT_DISCOUNT') {
      dependentControl.disable({ emitEvent: false });
      dependentControl.setValue(null);
    } else {
      dependentControl.enable({ emitEvent: false });
    }
  }

  private tryAutoSelect(
    field: FormField,
    dependentControl: any,
    value: any,
  ): boolean {
    if (field.autoSelect && typeof field.autoSelect === 'function') {
      const autoSelectedValue = field.autoSelect(value);
      if (autoSelectedValue !== undefined && autoSelectedValue !== null) {
        dependentControl.setValue(autoSelectedValue);
        return true;
      }
    }
    return false;
  }

  // ── getExistingFiles helper methods ─────────────────────────────────────────

  private toExistingFileEntry(file: any, index: number): any {
    const hasRuntimeFile = file?.file instanceof File;
    const inferredIsExisting = !hasRuntimeFile;

    return {
      id: file.id || `existing-${index}`,
      previewUrl: file.previewUrl || file.url,
      fileName:
        file.fileName ||
        this.extractFileNameFromUrl(file.previewUrl || file.url),
      fileSize: file.fileSize || 0,
      file: file.file,
      isExisting: file.isExisting ?? inferredIsExisting,
      onFileChanged: file.onFileChanged || false,
      objectName: file.objectName,
    };
  }

  private convertMultiFileEntry(file: any, index: number): any {
    if (typeof file === 'string') {
      return {
        id: `existing-${index}`,
        previewUrl: file,
        fileName: this.extractFileNameFromUrl(file),
        fileSize: 0,
        isExisting: true,
        onFileChanged: false,
      };
    }
    return this.toExistingFileEntry(file, index);
  }

  private getExistingSingleFile(fieldName: string): any[] {
    const formValue = this.form.get(fieldName)?.value;
    if (formValue && typeof formValue === 'object' && formValue.previewUrl) {
      const converted = this.toExistingFileEntry(formValue, 0);
      this.existingFilesCache.set(fieldName, [converted]);
      return [converted];
    }

    const cached = this.existingFilesCache.get(fieldName);
    if (cached && cached.length > 0) return cached;

    const initial = this.initialData?.[fieldName];
    if (initial && typeof initial === 'object' && initial.previewUrl) {
      const converted = this.toExistingFileEntry(initial, 0);
      this.existingFilesCache.set(fieldName, [converted]);
      return [converted];
    }

    return [];
  }

  private getExistingMultiFiles(fieldName: string): any[] {
    if (
      fieldName === 'detailImages' &&
      Array.isArray(this.initialData?.detailImages) &&
      this.initialData.detailImages.length === 0
    ) {
      return [];
    }

    const formValue = this.form.get(fieldName)?.value || [];
    if (Array.isArray(formValue) && formValue.length > 0) {
      const converted = formValue.map((file: any, i: number) =>
        this.convertMultiFileEntry(file, i),
      );
      this.existingFilesCache.set(fieldName, converted);
      return converted;
    }

    const cached = this.existingFilesCache.get(fieldName);
    if (cached && cached.length > 0) return cached;

    const initialValue = this.initialData?.[fieldName];
    if (Array.isArray(initialValue) && initialValue.length > 0) {
      const converted = initialValue.map((file: any, i: number) =>
        this.toExistingFileEntry(file, i),
      );
      this.existingFilesCache.set(fieldName, converted);
      return converted;
    }

    return [];
  }

  // ── getFormDataForSubmission helper methods ──────────────────────────────────

  private processMainImageForSubmission(processedData: any): void {
    if (!processedData.mainImage) return;
    if (Array.isArray(processedData.mainImage)) {
      const changedFiles = processedData.mainImage
        .filter((file: any) => file && (file.onFileChanged || file.file))
        .map((file: any) => file.file || file);
      if (changedFiles.length > 0) {
        processedData.mainImage = changedFiles;
      } else {
        delete processedData.mainImage;
      }
    } else if (processedData.mainImage.onFileChanged) {
      processedData.mainImage = [processedData.mainImage.file];
    } else {
      delete processedData.mainImage;
    }
  }

  /**
   * Collects new-upload/removal submission data from every multi-file field in the form,
   * bucketed by field controlName (component.fieldName). Forms with a single multi-file field
   * (the common case, historically always named "detailImages") get one bucket; forms with more
   * than one multi-file field get one bucket each.
   */
  private collectMultiFileImagesData(): Record<
    string,
    { files: any[]; removed: string[] }
  > {
    const buckets: Record<string, { files: any[]; removed: string[] }> = {};
    if (this.multiFileUploadComponents) {
      this.multiFileUploadComponents.forEach((component) => {
        if (!component.getSubmissionData) {
          return;
        }
        const fieldName: string = component.fieldName || 'detailImages';
        const submissionData = component.getSubmissionData();
        const bucket = (buckets[fieldName] ??= { files: [], removed: [] });
        if (submissionData.detailImages?.length > 0) {
          bucket.files.push(...submissionData.detailImages);
        }
        if (submissionData.removeDetailImages?.length > 0) {
          bucket.removed.push(...submissionData.removeDetailImages);
        }
      });
    }
    return buckets;
  }
}
